import React, { useEffect, useState } from 'react';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Link as LinkIcon, 
  Plus, 
  Trash2, 
  Check, 
  Copy, 
  ExternalLink,
  Shield,
  DollarSign,
  Globe,
  Loader2,
  Pencil,
  ChevronDown,
  Instagram,
  Youtube,
  User,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Phone,
  ArrowRight,
  MessageSquare,
  CheckCircle,
  Palette,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { AllodosLogo } from './AllodosLogo';
import { BookingLandingAside, ProfessionalAvatar, ProfessionalCard, isHexColor, isStockPhoto } from './BookingLanding';
import {
  DEFAULT_RECESS_WHATSAPP_TEMPLATE,
  fetchAgendaConfigFor,
  fetchMyBookingPage,
  publishBookingPage,
  saveAgendaConfigFor,
  type BookingConfig,
  type PublishedBookingPage,
  type RecessConfig,
} from '../services/bookingApi';
import { describeError } from '../utils/describeError';
import { canManageBookingPage } from '../utils/rolePermissions';
import { isProfessionalRole } from '../utils/patientAccess';
import { scopedGet, scopedKey, scopedRemove, scopedSet } from '../utils/scopedStorage';

const Tiktok: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const FONT_FAMILIES = [
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  { name: 'Courier New', value: "'Courier New', Courier, monospace" },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { name: 'Trebuchet MS', value: "'Trebuchet MS', Helvetica, sans-serif" },
  { name: 'Comic Sans MS', value: "'Comic Sans MS', cursive, sans-serif" },
  { name: 'Impact', value: 'Impact, Charcoal, sans-serif' },
  { name: 'Garamond', value: 'Garamond, Baskerville, serif' },
  { name: 'Bookman', value: "'Bookman Old Style', Bookman, Garamond, serif" },
];

const FONT_SIZES = [
  { name: '10px (Muito Pequeno)', value: '10px' },
  { name: '11px (Pequeno)', value: '11px' },
  { name: '12px (Médio)', value: '12px' },
  { name: '14px (Normal)', value: '14px' },
  { name: '16px (Destaque)', value: '16px' },
  { name: '18px (Grande)', value: '18px' },
  { name: '20px (Muito Grande)', value: '20px' },
  { name: '24px (Título)', value: '24px' },
];

const MONTHS_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

interface Professional {
  id: string;
  name: string;
  formation: string;
  crp: string;
  photo: string;
  bio?: string;
  /**
   * Se o profissional aparece na página pública de agendamento.
   * Ausente = ativo, para não esconder quem já estava cadastrado.
   */
  active?: boolean;
}

/** Chave compartilhada com o livro caixa (categorias de entrada). */
export const SERVICES_STORAGE_KEY = 'allodos-agenda-services';
/** Locais de atendimento e horários persistidos localmente (e publicados na API). */
export const LOCATIONS_STORAGE_KEY = 'allodos-agenda-locations';
export const HOURS_STORAGE_KEY = 'allodos-agenda-hours';

function readJson<T>(key: string): T | null {
  try {
    const saved = localStorage.getItem(key);
    return saved ? (JSON.parse(saved) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Bloco 36: lista de profissionais/administrador com caixa seletora por nome e
 * "Selecionar todos", usada ao criar/editar serviços e locais.
 */
const ProfessionalPicker: React.FC<{
  question: string;
  professionals: { id: string; name: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ question, professionals, selected, onChange }) => {
  const allIds = professionals.map((p) => String(p.id));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const checkboxClass =
    'w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-pointer';
  return (
    <fieldset className="space-y-2 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
      <legend className="px-2 text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">{question}</legend>
      <label className="flex items-center gap-3 text-xs font-black text-indigo-600 dark:text-indigo-400 cursor-pointer">
        <input type="checkbox" className={checkboxClass} checked={allSelected} onChange={() => onChange(allSelected ? [] : allIds)} />
        Selecionar todos
      </label>
      <div className="grid grid-cols-1 gap-1.5 pl-1">
        {professionals.map((p) => {
          const id = String(p.id);
          return (
            <label key={id} className="flex items-center gap-3 text-xs font-semibold text-slate-700 dark:text-slate-200 cursor-pointer">
              <input type="checkbox" className={checkboxClass} checked={selected.includes(id)} onChange={() => toggle(id)} />
              {p.name}
            </label>
          );
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 font-bold">Nenhum selecionado: o item não aparecerá no link de nenhum profissional.</p>
      )}
    </fieldset>
  );
};

/**
 * Bloco #61: foto do profissional na agenda e no link externo = a de
 * "Identidade do Profissional" (perfil). Sem ela, a escolhida aqui na agenda;
 * fotos de banco de imagens (antigo padrão) não contam.
 */
function profilePhoto(prof: any, customPhoto?: string, legacyMainPhoto?: string): string {
  const candidates = [prof?.professionalPhoto, prof?.avatarUrl, prof?.id === '1' ? legacyMainPhoto : undefined, prof?.photo, customPhoto];
  return candidates.find((p) => p && !isStockPhoto(p)) || '';
}

interface Service {
  id: string;
  name: string;
  duration: number; // minutes
  price: number;
  description: string;
  active?: boolean;
  /** Bloco 36: profissionais (e administrador) que oferecem o serviço. Vazio/ausente = todos. */
  professionalIds?: string[];
}

interface Location {
  id: string;
  name: string;
  type: 'online' | 'presential';
  address?: string;
  link?: string;
  active?: boolean;
  /** Bloco 36: profissionais (e administrador) que atendem neste local. Vazio/ausente = todos. */
  professionalIds?: string[];
}

interface TimeSlot {
  id: string;
  start: string;
  end: string;
  /** Bloco #67: PRESENCIAL_ONLINE deixa o paciente escolher entre os dois locais. */
  modality: 'PRESENCIAL' | 'ONLINE' | 'PRESENCIAL_ONLINE';
  /** Local da faixa; na PRESENCIAL_ONLINE, o local presencial. */
  locationId: string;
  /** Bloco #67: teleconsulta da faixa PRESENCIAL_ONLINE. */
  onlineLocationId?: string;
  serviceIds: string[];
}

interface DayHours {
  day: string;
  active: boolean;
  slots: TimeSlot[];
}

interface AgendaSettingsProps {
  userProfiles?: any[];
  /** Perfil logado: define escopo dos dados e o acesso à página pública. */
  activeProfile?: { id?: string; role?: string; name?: string } | null;
  /** Nome da clínica (do perfil autenticado), exibido na página pública. */
  clinicName?: string;
}

export const AgendaSettings: React.FC<AgendaSettingsProps> = ({ userProfiles: userProfilesProp = [], activeProfile = null, clinicName = '' }) => {
  const [activeTab, setActiveTab] = useState<'services' | 'locations' | 'hours' | 'booking-link'>('services');

  /**
   * Profissional: configuração própria, começando do zero (sem exemplos do
   * sistema) e guardada em chaves separadas por usuário. A página pública de
   * agendamento é da clínica — só administrador e recepção a configuram.
   */
  const isProfessional = isProfessionalRole(activeProfile?.role);
  const canPublish = canManageBookingPage(activeProfile?.role) || !activeProfile?.role;
  const scopeSuffix = isProfessional && activeProfile?.id ? `:${activeProfile.id}` : '';
  // Bloco #77: toda chave local é isolada pela clínica autenticada
  // (`<chave>@<tenantId>`); outra conta no mesmo navegador não herda nada.
  const servicesKey = scopedKey(`${SERVICES_STORAGE_KEY}${scopeSuffix}`);
  const locationsKey = scopedKey(`${LOCATIONS_STORAGE_KEY}${scopeSuffix}`);
  const hoursKey = scopedKey(`${HOURS_STORAGE_KEY}${scopeSuffix}`);

  // Serviços cadastrados. Ficam persistidos porque a agenda e o livro caixa
  // (categorias de entrada) dependem deles. Bloco #77: uma conta nova começa
  // vazia — não há mais exemplos do sistema, e o que já foi publicado volta do
  // servidor (adoptPublishedPage).
  const [services, setServices] = useState<Service[]>(() => {
    try {
      const saved = localStorage.getItem(servicesKey);
      const parsed = saved ? JSON.parse(saved) : null;
      if (Array.isArray(parsed)) return parsed as Service[];
    } catch { /* cache inválido */ }
    return [];
  });

  useEffect(() => {
    localStorage.setItem(servicesKey, JSON.stringify(services));
  }, [services, servicesKey]);

  const [locations, setLocations] = useState<Location[]>(() => {
    const saved = readJson<Location[]>(locationsKey);
    return Array.isArray(saved) ? saved : [];
  });

  useEffect(() => {
    localStorage.setItem(locationsKey, JSON.stringify(locations));
  }, [locations, locationsKey]);

  const DAY_NAMES = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

  // Horários: o estado é o rascunho da tela; o que vale (e vai para o link
  // externo) é o que foi salvo com "Salvar Alterações". Antes não havia botão
  // e as edições sumiam ao recarregar.
  // Bloco #77: sem grade de exemplo — conta nova abre os horários vazios.
  const EMPTY_HOURS: DayHours[] = DAY_NAMES.map((day) => ({ day, active: false, slots: [] }));
  const initialHours = (): DayHours[] => {
    const saved = readJson<DayHours[]>(hoursKey);
    if (Array.isArray(saved) && saved.length) return saved;
    return EMPTY_HOURS;
  };
  const [hours, setHours] = useState<DayHours[]>(initialHours);
  const [savedHoursJson, setSavedHoursJson] = useState<string>(() => JSON.stringify(initialHours()));
  const hoursDirty = JSON.stringify(hours) !== savedHoursJson;
  const [isSavingHours, setIsSavingHours] = useState(false);
  const [hoursFeedback, setHoursFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // Página já publicada na API (se houver): é ela que o paciente enxerga.
  const [publishedPage, setPublishedPage] = useState<PublishedBookingPage | null>(null);

  // Alerta ao sair com alterações não salvas nos horários.
  useEffect(() => {
    if (!hoursDirty) return;
    const aviso = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [hoursDirty]);

  const [openServiceDropdownId, setOpenServiceDropdownId] = useState<string | null>(null);
  const [copySourceDayIndex, setCopySourceDayIndex] = useState<number | null>(null);
  const [copyTargetDays, setCopyTargetDays] = useState<{[key: string]: boolean}>({});

  const [slug, setSlug] = useState(() => {
    const saved = scopedGet('allodos-booking-slug');
    return saved || '';
  });
  // Link do portal público de agendamento. Antes apontava para um domínio que
  // não serve a aplicação (`allodos.com/agendar/...`) e caía no painel.
  const bookingLinkPrefix = `${window.location.host}/?agendar=`;
  const bookingLink = `${window.location.origin}/?agendar=${encodeURIComponent(slug)}`;
  const [copied, setCopied] = useState(false);

  // Bloco #64: Modo Recesso / Férias (vai na página publicada; o link externo
  // troca a grade pelo formulário de lista de espera enquanto estiver ativo).
  const [recess, setRecess] = useState<RecessConfig>(() => {
    try {
      const saved = scopedGet('allodos-recess');
      if (saved) return { enabled: false, whatsappTemplate: DEFAULT_RECESS_WHATSAPP_TEMPLATE, ...JSON.parse(saved) } as RecessConfig;
    } catch { /* ignore */ }
    return { enabled: false, returnDate: '', notice: '', whatsappTemplate: DEFAULT_RECESS_WHATSAPP_TEMPLATE };
  });
  const updateRecess = (patch: Partial<RecessConfig>) => {
    setRecess((prev) => {
      const next = { ...prev, ...patch };
      scopedSet('allodos-recess', JSON.stringify(next));
      return next;
    });
  };

  // New scheduling configuration fields from corporas screen
  const [autoBooking, setAutoBooking] = useState<boolean>(() => {
    const saved = scopedGet('allodos-auto-booking');
    return saved === null ? true : saved === 'true';
  });
  const [openDaysLimit, setOpenDaysLimit] = useState<number>(() => {
    const saved = scopedGet('allodos-open-days-limit');
    return saved ? parseInt(saved, 10) : 60;
  });
  const [minInterval, setMinInterval] = useState<number>(() => {
    const saved = scopedGet('allodos-min-interval');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [minAdvanceHours, setMinAdvanceHours] = useState<number>(() => {
    const saved = scopedGet('allodos-min-advance-hours');
    return saved ? parseInt(saved, 10) : 12;
  });

  // Social Links
  const [instagram, setInstagram] = useState(() => scopedGet('allodos-social-instagram') || '');
  const [tiktok, setTiktok] = useState(() => scopedGet('allodos-social-tiktok') || '');
  const [youtube, setYoutube] = useState(() => scopedGet('allodos-social-youtube') || '');

  // Custom Links
  const [customLinks, setCustomLinks] = useState<{ id: string; name: string; url: string }[]>(() => {
    const saved = scopedGet('allodos-custom-links');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { }
    }
    return [];
  });

  // State to control add custom link form
  const [showAddCustomLink, setShowAddCustomLink] = useState(false);
  const [newCustomLinkName, setNewCustomLinkName] = useState('');
  const [newCustomLinkUrl, setNewCustomLinkUrl] = useState('');

  // Booking page customization states
  const [welcomeMessage, setWelcomeMessage] = useState(() => {
    return scopedGet('allodos-welcome-message') || 
      'Seja bem vindo a agenda on-line. Escolha ao lado com qual profissional você gostaria de ser atendido.';
  });

  const [rightSideStyle, setRightSideStyle] = useState<'blank' | 'custom-text'>(() => {
    const saved = scopedGet('allodos-right-style') || 'custom-text';
    return (saved === 'gradient' ? 'custom-text' : saved) as 'blank' | 'custom-text';
  });

  const [rightSideBgType, setRightSideBgType] = useState<'blank' | 'gradient' | 'image'>(() => {
    const savedStyle = scopedGet('allodos-right-style');
    const savedBgType = scopedGet('allodos-right-bg-type');
    if (savedBgType) return savedBgType as 'blank' | 'gradient' | 'image';
    if (savedStyle === 'gradient') return 'gradient';
    return 'gradient';
  });

  const [gradientColor1, setGradientColor1] = useState(() => scopedGet('allodos-right-gradient-1') || '#6366f1');
  const [gradientColor2, setGradientColor2] = useState(() => scopedGet('allodos-right-gradient-2') || '#a855f7');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(() => {
    return scopedGet('allodos-right-bg-image') || 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&q=80&w=1000';
  });

  const [bgTransparency, setBgTransparency] = useState<number>(() => {
    return Number(scopedGet('allodos-right-bg-transparency') || '80');
  });

  const [isDragging, setIsDragging] = useState(false);

  const handleImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setBackgroundImageUrl(result);
        scopedSet('allodos-right-bg-image', result);
      }
    };
    reader.readAsDataURL(file);
  };

  const [rightSideText, setRightSideText] = useState(() => {
    return scopedGet('allodos-right-text') || 
      'Oferecemos psicoterapia especializada com profissionais qualificados. Agende sua consulta em poucos cliques de forma segura e humanizada.';
  });

  const [compFontFamily, setCompFontFamily] = useState(() => {
    return scopedGet('allodos-comp-font-family') || "Arial, sans-serif";
  });
  const [compFontSize, setCompFontSize] = useState(() => {
    return scopedGet('allodos-comp-font-size') || "11px";
  });
  const [compIsBold, setCompIsBold] = useState(() => {
    return scopedGet('allodos-comp-is-bold') === 'true';
  });
  const [compIsItalic, setCompIsItalic] = useState(() => {
    return scopedGet('allodos-comp-is-italic') === 'true';
  });
  const [compIsUnderline, setCompIsUnderline] = useState(() => {
    return scopedGet('allodos-comp-is-underline') === 'true';
  });
  // Bloco #69: cor do texto da faixa lateral. Vazio = branco (aparência atual).
  const [compTextColor, setCompTextColor] = useState(() => {
    const saved = scopedGet('allodos-comp-text-color') || '';
    return isHexColor(saved) ? saved : '';
  });
  const [compTextColorDraft, setCompTextColorDraft] = useState(compTextColor || '#FFFFFF');
  const [compTextColorError, setCompTextColorError] = useState<string | null>(null);
  const applyTextColor = (value: string) => {
    const v = value.trim();
    setCompTextColorDraft(v);
    if (!isHexColor(v)) {
      setCompTextColorError('Cor inválida. Use o formato #RRGGBB (ex.: #FFFFFF). Mantida a última cor válida.');
      return;
    }
    setCompTextColorError(null);
    setCompTextColor(v.toUpperCase());
    scopedSet('allodos-comp-text-color', v.toUpperCase());
  };

  const [professionals, setProfessionals] = useState<Professional[]>(() => {
    // 1. Load active user profiles from localStorage
    const savedProfiles = localStorage.getItem('allodos-user-profiles');
    let userProfs: any[] = Array.isArray(userProfilesProp) ? userProfilesProp : [];
    if (userProfs.length === 0 && savedProfiles) {
      try {
        userProfs = JSON.parse(savedProfiles);
      } catch (e) {}
    }

    // Filter out receptionist roles (Recepcionista, Secretária, etc.)
    const activeProfs = userProfs.filter((p: any) => {
      const roleLower = (p.role || '').toLowerCase();
      return !roleLower.includes('recep') && !roleLower.includes('secretar');
    });

    // 2. Load therapist config for main CRP and main photo
    const savedTherapist = scopedGet('allodos-therapist');
    let therapistObj: any = null;
    if (savedTherapist) {
      try {
        therapistObj = JSON.parse(savedTherapist);
      } catch (e) {}
    }
    const mainCrp = therapistObj?.crp || '';
    const mainPhoto = therapistObj?.professionalPhoto || '';

    // 3. Load saved custom details (formation, bio)
    const savedProfs = scopedGet('allodos-professionals');
    let customProfs: Professional[] = [];
    if (savedProfs) {
      try {
        customProfs = JSON.parse(savedProfs);
      } catch (e) {}
    }

    // 4. Map user profiles to Professional structures
    return activeProfs.map((prof: any) => {
      const custom = customProfs.find(c => c.id === prof.id);
      
      // Determine CRP: If ID is '1', pull from therapist.crp. Otherwise, try user profile or custom or default
      let crp = '';
      if (prof.id === '1') {
        crp = !mainCrp ? '' : mainCrp.startsWith('CRP') ? mainCrp : `CRP ${mainCrp}`;
      } else {
        crp = prof.crp || custom?.crp || ''; // Bloco #77: sem CRP fica em branco, nunca um número inventado
      }

      // Bloco #61: a foto de "Identidade do Profissional" (professionalPhoto do
      // perfil) é a referência; sem ela, avatar neutro (nunca foto de banco).
      const photo = profilePhoto(prof, custom?.photo, mainPhoto);

      return {
        id: prof.id,
        name: prof.name,
        formation: custom?.formation || (prof.id === '1' ? 'Psicóloga Clínica (TCC) e Mestre em Saúde Mental' : 'Psicólogo(a) Clínico(a)'),
        crp: crp,
        photo: photo,
        bio: custom?.bio || 'Profissional de atendimento dedicado ao cuidado integral e desenvolvimento do paciente.',
        // Preserva a escolha de exibir ou não na página de agendamento: a
        // sincronização com os perfis do sistema não pode reativar quem foi
        // ocultado de propósito.
        active: custom?.active !== false,
      };
    });
  });

  // State for showing the Professional Form (add/edit)
  const [showProfessionalForm, setShowProfessionalForm] = useState(false);
  const [editingProf, setEditingProf] = useState<Professional | null>(null);
  const [profName, setProfName] = useState('');
  const [profFormation, setProfFormation] = useState('');
  const [profCrp, setProfCrp] = useState('');
  const [profPhoto, setProfPhoto] = useState('');
  const [profBio, setProfBio] = useState('');

  // Auto-sync effect to keep professionals list dynamically updated with system profiles
  React.useEffect(() => {
    const syncProfs = () => {
      const savedProfiles = localStorage.getItem('allodos-user-profiles');
      try {
        const userProfs = userProfilesProp.length ? userProfilesProp : savedProfiles ? JSON.parse(savedProfiles) : [];
        if (!userProfs.length) return;
        const activeProfs = userProfs.filter((p: any) => {
          const roleLower = (p.role || '').toLowerCase();
          // Convites pendentes (id "inv:…") ainda não são usuários: a API recusa
          // publicar a página com eles ("profissional de outra clínica").
          if (String(p.id || '').startsWith('inv:')) return false;
          return !roleLower.includes('recep') && !roleLower.includes('secretar');
        });
        
        const savedTherapist = scopedGet('allodos-therapist');
        const therapistObj = savedTherapist ? JSON.parse(savedTherapist) : null;
        const mainCrp = therapistObj?.crp || '';
        const mainPhoto = therapistObj?.professionalPhoto || '';

        setProfessionals(prev => {
          return activeProfs.map((prof: any) => {
            const custom = prev.find(c => c.id === prof.id);
            let crp = '';
            if (prof.id === '1') {
              crp = !mainCrp ? '' : mainCrp.startsWith('CRP') ? mainCrp : `CRP ${mainCrp}`;
            } else {
              crp = prof.crp || custom?.crp || ''; // Bloco #77: sem CRP fica em branco, nunca um número inventado
            }

            const photo = profilePhoto(prof, custom?.photo, mainPhoto);

            return {
              id: prof.id,
              name: prof.name,
              formation: custom?.formation || (prof.id === '1' ? 'Psicóloga Clínica (TCC) e Mestre em Saúde Mental' : 'Psicólogo(a) Clínico(a)'),
              crp: crp,
              photo: photo,
              bio: custom?.bio || 'Profissional de atendimento dedicado ao cuidado integral e desenvolvimento do paciente.',
              active: custom?.active !== false,
            };
          });
        });
      } catch (e) {}
    };

    // Bloco #61: perfis atualizados (ex.: foto nova) refletem na hora.
    syncProfs();
    window.addEventListener('focus', syncProfs);
    return () => window.removeEventListener('focus', syncProfs);
  }, [userProfilesProp]);

  // Live Interactive Patient Booking Experience Simulation State
  const sortedProfs = React.useMemo(() => {
    return [...professionals].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [professionals]);

  /** Só os ativos aparecem na página de agendamento vista pelo paciente. */
  const bookingProfs = React.useMemo(
    () => sortedProfs.filter((prof) => prof.active !== false),
    [sortedProfs],
  );

  /** Bloco 36: ids de todos os profissionais/administrador (padrão "Selecionar todos"). */
  const allProfessionalIds = React.useMemo(() => sortedProfs.map((p) => String(p.id)), [sortedProfs]);

  /** Nomes dos profissionais vinculados a um serviço/local ("Todos" quando não restringe). */
  const professionalNamesFor = (ids?: string[]): string => {
    if (!ids || ids.length === 0) return 'Todos';
    const nomes = sortedProfs.filter((p) => ids.includes(String(p.id))).map((p) => p.name);
    return nomes.length ? nomes.join(', ') : 'Todos';
  };

  // Formulários novos já começam com todos os profissionais marcados.
  useEffect(() => {
    if (!editingService && newServiceProfIds.length === 0 && allProfessionalIds.length) setNewServiceProfIds(allProfessionalIds);
    if (!editingLocation && newLocProfIds.length === 0 && allProfessionalIds.length) setNewLocProfIds(allProfessionalIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProfessionalIds]);

  const toggleProfessionalActive = (id: string) => {
    setProfessionals((prev) => {
      const updated = prev.map((prof) =>
        prof.id === id ? { ...prof, active: prof.active === false } : prof,
      );
      scopedSet('allodos-professionals', JSON.stringify(updated));
      return updated;
    });
  };

  const [previewStep, setPreviewStep] = useState<'landing' | 'calendar' | 'popup' | 'whatsapp' | 'success'>('landing');
  const [selectedProf, setSelectedProf] = useState<Professional | null>(null);
  const [currentMonth, setCurrentMonth] = useState(6); // 6 = Julho
  const [currentYear, setCurrentYear] = useState(2026);
  const [selectedDay, setSelectedDay] = useState<number | null>(15); // Default to a standard weekday
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [patientName, setPatientName] = useState('João Silva');
  const [patientPhone, setPatientPhone] = useState('(11) 99999-9999');
  const [selectedModality, setSelectedModality] = useState<'presencial' | 'online'>('online');
  const [selectedServiceId, setSelectedServiceId] = useState('1'); // Default to first service

  const handlePrevMonth = () => {
    setSelectedDay(null);
    setSelectedTimeSlot(null);
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prev => prev - 1);
    } else {
      setCurrentMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedDay(null);
    setSelectedTimeSlot(null);
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prev => prev + 1);
    } else {
      setCurrentMonth(prev => prev + 1);
    }
  };

  const getAvailableSlotsForPreview = (dayNum: number): string[] => {
    const weekdayIndex = new Date(currentYear, currentMonth, dayNum).getDay(); 
    const hoursIndexMap = [6, 0, 1, 2, 3, 4, 5];
    const hoursDayIndex = hoursIndexMap[weekdayIndex];
    const dayConfig = hours[hoursDayIndex];
    if (!dayConfig || !dayConfig.active || !dayConfig.slots) return [];

    const slotsToReturn: string[] = [];
    const selectedService = services.find(s => s.id === selectedServiceId) || services[0];
    const duration = selectedService ? selectedService.duration : 50;
    const step = duration + minInterval;

    dayConfig.slots.forEach(slot => {
      const [startH, startM] = slot.start.split(':').map(Number);
      const [endH, endM] = slot.end.split(':').map(Number);
      
      let startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;

      while (startMinutes + duration <= endMinutes) {
        const h = Math.floor(startMinutes / 60);
        const m = startMinutes % 60;
        const hStr = h.toString().padStart(2, '0');
        const mStr = m.toString().padStart(2, '0');
        slotsToReturn.push(`${hStr}:${mStr}`);
        startMinutes += step;
      }
    });

    return slotsToReturn;
  };

  // Form states for adding/editing items
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState(50);
  const [newServicePrice, setNewServicePrice] = useState(150);
  const [newServiceDesc, setNewServiceDesc] = useState('');
  const [newServiceProfIds, setNewServiceProfIds] = useState<string[]>([]);
  const [editingService, setEditingService] = useState<Service | null>(null);

  const [newLocName, setNewLocName] = useState('');
  const [newLocType, setNewLocType] = useState<'online' | 'presential'>('online');
  const [newLocAddress, setNewLocAddress] = useState('');
  // Bloco 57: link da sala virtual (Google Meet) do local online — é ele que
  // vai ao paciente por WhatsApp/e-mail ao clicar em "Acessar Sala".
  const [newLocLink, setNewLocLink] = useState('');
  const [newLocProfIds, setNewLocProfIds] = useState<string[]>([]);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);

  // Bloco 36 — "Configurar Horários": de qual profissional/administrador os
  // horários exibidos são. Administrador e recepção escolhem; os horários de
  // cada um ficam no servidor e valem no link externo dele.
  const [hoursProfessionalId, setHoursProfessionalId] = useState<string>(() => String(activeProfile?.id ?? ''));
  const [hoursProfessionalLoading, setHoursProfessionalLoading] = useState(false);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishFeedback, setPublishFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  /**
   * Junta a configuração da tela no formato publicado.
   * É este objeto que o paciente enxerga — por isso ele vai para a API, e não
   * fica só no localStorage deste navegador.
   */
  const buildBookingConfig = (hoursToPublish: DayHours[] = hours): BookingConfig => ({
    clinic: {
      // Nome da clínica vem do perfil autenticado (App), nunca de cache global.
      name: clinicName || publishedPage?.config?.clinic?.name || 'Agende sua consulta',
      welcomeMessage,
      complementaryText: rightSideText,
      links: customLinks.map((l) => ({ label: l.name, url: l.url })),
      social: { instagram, tiktok, youtube },
    },
    appearance: {
      backgroundType: rightSideBgType,
      gradientFrom: gradientColor1,
      gradientTo: gradientColor2,
      imageUrl: backgroundImageUrl,
      transparency: bgTransparency,
      fontFamily: compFontFamily,
      fontSize: compFontSize,
      bold: compIsBold,
      italic: compIsItalic,
      underline: compIsUnderline,
      sideContent: rightSideStyle,
      ...(compTextColor ? { textColor: compTextColor } : {}),
    },
    services: services.filter((s) => s.active !== false),
    locations: locations.filter((l) => l.active !== false),
    professionals: bookingProfs,
    hours: hoursToPublish,
    openDaysLimit,
    slotIntervalMinutes: minInterval,
    minAdvanceHours,
    autoConfirm: autoBooking,
    recess: {
      enabled: !!recess.enabled,
      returnDate: recess.returnDate || null,
      notice: recess.notice || null,
      whatsappTemplate: recess.whatsappTemplate || DEFAULT_RECESS_WHATSAPP_TEMPLATE,
    },
  });

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishFeedback(null);
    try {
      if (!slug.trim()) {
        throw new Error('Informe o link público da clínica (ex.: nome-da-clinica) antes de publicar.');
      }
      const page = await publishBookingPage({ slug, config: buildBookingConfig() });
      setPublishedPage(page);
      // Publicar também consolida os horários exibidos.
      localStorage.setItem(hoursKey, JSON.stringify(hours));
      setSavedHoursJson(JSON.stringify(hours));
      setPublishFeedback({
        ok: true,
        msg: `Página publicada em ${window.location.host}/?agendar=${page.slug}`,
      });
      return page;
    } catch (err) {
      console.error(err);
      setPublishFeedback({ ok: false, msg: describeError(err) });
      throw err;
    } finally {
      setIsPublishing(false);
    }
  };

  /**
   * "Salvar Alterações" da aba Configurar Horários: persiste localmente e,
   * se a página já foi publicada, republica para o link externo refletir
   * na hora.
   */
  const handleSaveHours = async () => {
    setIsSavingHours(true);
    setHoursFeedback(null);
    try {
      localStorage.setItem(hoursKey, JSON.stringify(hours));
      setSavedHoursJson(JSON.stringify(hours));
      // Bloco 36: os horários valem para o profissional selecionado no filtro
      // e ficam no servidor — o link externo dele usa esta grade.
      const alvo = hoursProfessionalId || String(activeProfile?.id ?? '');
      if (alvo) {
        await saveAgendaConfigFor(alvo, { services: [], locations: [], hours });
      }
      const nomeAlvo = sortedProfs.find((p) => String(p.id) === alvo)?.name ?? 'o profissional';
      if (publishedPage && canPublish && alvo === String(activeProfile?.id ?? '')) {
        // Os horários de quem publica a página também são a grade padrão dela.
        const page = await publishBookingPage({
          slug: publishedPage.slug,
          config: buildBookingConfig(hours),
        });
        setPublishedPage(page);
        setHoursFeedback({
          ok: true,
          msg: 'Alterações salvas e já refletidas nos horários do link de agendamento.',
        });
      } else if (alvo) {
        setHoursFeedback({
          ok: true,
          msg: `Alterações salvas. Os horários de ${nomeAlvo} já valem no link de agendamento quando o paciente escolhe esse profissional.`,
        });
      } else {
        setHoursFeedback({
          ok: true,
          msg: 'Alterações salvas. Publique a página (aba "Configuração página de agendamento") para refletir no link externo.',
        });
      }
    } catch (err) {
      console.error(err);
      setHoursFeedback({ ok: false, msg: `Salvo neste dispositivo, mas não foi possível atualizar o link externo: ${describeError(err)}` });
    } finally {
      setIsSavingHours(false);
      window.setTimeout(() => setHoursFeedback(null), 6000);
    }
  };

  /**
   * Bloco #77: a página publicada no servidor (escopada pela clínica
   * autenticada) é a fonte da verdade. Tudo o que ela traz substitui o cache
   * deste navegador — nunca o contrário —, para uma conta não herdar nem
   * republicar dados que não são dela. Só os horários do rascunho local
   * prevalecem, porque a grade do próprio usuário vem de loadHoursFor.
   */
  const adoptPublishedPage = (page: PublishedBookingPage) => {
    const cfg = page.config || {};
    if (page.slug) {
      setSlug(page.slug);
      scopedSet('allodos-booking-slug', page.slug);
    }
    if (Array.isArray(cfg.services)) setServices(cfg.services as Service[]);
    if (Array.isArray(cfg.locations)) setLocations(cfg.locations as Location[]);
    if (!readJson(hoursKey) && Array.isArray(cfg.hours) && cfg.hours.length) {
      const remotos = cfg.hours as DayHours[];
      setHours(remotos);
      setSavedHoursJson(JSON.stringify(remotos));
      localStorage.setItem(hoursKey, JSON.stringify(remotos));
    }
    if (Array.isArray(cfg.professionals) && cfg.professionals.length) {
      const publicados = cfg.professionals as Professional[];
      scopedSet('allodos-professionals', JSON.stringify(publicados));
      setProfessionals((prev) =>
        prev.map((p) => {
          const pub = publicados.find((x) => String(x.id) === String(p.id));
          return pub
            ? { ...p, formation: pub.formation || p.formation, bio: pub.bio ?? p.bio, photo: pub.photo || p.photo, active: pub.active }
            : p;
        }),
      );
    }
    const clinic = cfg.clinic || {};
    if (typeof clinic.welcomeMessage === 'string') {
      setWelcomeMessage(clinic.welcomeMessage);
      scopedSet('allodos-welcome-message', clinic.welcomeMessage);
    }
    if (typeof clinic.complementaryText === 'string') {
      setRightSideText(clinic.complementaryText);
      scopedSet('allodos-right-text', clinic.complementaryText);
    }
    if (Array.isArray(clinic.links)) {
      const links = clinic.links.map((l, i) => ({ id: String(i + 1), name: String(l?.label || ''), url: String(l?.url || '') }));
      setCustomLinks(links);
      scopedSet('allodos-custom-links', JSON.stringify(links));
    }
    if (clinic.social && typeof clinic.social === 'object') {
      const social = { instagram: clinic.social.instagram || '', tiktok: clinic.social.tiktok || '', youtube: clinic.social.youtube || '' };
      setInstagram(social.instagram);
      setTiktok(social.tiktok);
      setYoutube(social.youtube);
      scopedSet('allodos-social-instagram', social.instagram);
      scopedSet('allodos-social-tiktok', social.tiktok);
      scopedSet('allodos-social-youtube', social.youtube);
    }
    const ap = cfg.appearance || {};
    if (ap.backgroundType) {
      setRightSideBgType(ap.backgroundType);
      scopedSet('allodos-right-bg-type', ap.backgroundType);
    }
    if (ap.gradientFrom) {
      setGradientColor1(ap.gradientFrom);
      scopedSet('allodos-right-gradient-1', ap.gradientFrom);
    }
    if (ap.gradientTo) {
      setGradientColor2(ap.gradientTo);
      scopedSet('allodos-right-gradient-2', ap.gradientTo);
    }
    if (ap.imageUrl) {
      setBackgroundImageUrl(ap.imageUrl);
      scopedSet('allodos-right-bg-image', ap.imageUrl);
    }
    if (typeof ap.transparency === 'number') {
      setBgTransparency(ap.transparency);
      scopedSet('allodos-right-bg-transparency', String(ap.transparency));
    }
    if (ap.fontFamily) {
      setCompFontFamily(ap.fontFamily);
      scopedSet('allodos-comp-font-family', ap.fontFamily);
    }
    if (typeof ap.textColor === 'string') {
      setCompTextColor(ap.textColor);
      setCompTextColorDraft(ap.textColor || '#FFFFFF');
      scopedSet('allodos-comp-text-color', ap.textColor);
    }
    if (ap.fontSize) {
      setCompFontSize(ap.fontSize);
      scopedSet('allodos-comp-font-size', ap.fontSize);
    }
    if (typeof ap.bold === 'boolean') {
      setCompIsBold(ap.bold);
      scopedSet('allodos-comp-is-bold', String(ap.bold));
    }
    if (typeof ap.italic === 'boolean') {
      setCompIsItalic(ap.italic);
      scopedSet('allodos-comp-is-italic', String(ap.italic));
    }
    if (typeof ap.underline === 'boolean') {
      setCompIsUnderline(ap.underline);
      scopedSet('allodos-comp-is-underline', String(ap.underline));
    }
    if (typeof cfg.openDaysLimit === 'number') {
      setOpenDaysLimit(cfg.openDaysLimit);
      scopedSet('allodos-open-days-limit', String(cfg.openDaysLimit));
    }
    if (typeof cfg.slotIntervalMinutes === 'number') {
      setMinInterval(cfg.slotIntervalMinutes);
      scopedSet('allodos-min-interval', String(cfg.slotIntervalMinutes));
    }
    if (typeof cfg.minAdvanceHours === 'number') {
      setMinAdvanceHours(cfg.minAdvanceHours);
      scopedSet('allodos-min-advance-hours', String(cfg.minAdvanceHours));
    }
    if (typeof cfg.autoConfirm === 'boolean') {
      setAutoBooking(cfg.autoConfirm);
      scopedSet('allodos-auto-booking', String(cfg.autoConfirm));
    }
    // Bloco #64: o estado do recesso é o publicado (vale em qualquer dispositivo).
    if (cfg.recess && typeof cfg.recess === 'object') {
      const r = cfg.recess as RecessConfig;
      const next = { enabled: !!r.enabled, returnDate: r.returnDate || '', notice: r.notice || '', whatsappTemplate: r.whatsappTemplate || DEFAULT_RECESS_WHATSAPP_TEMPLATE };
      setRecess(next);
      scopedSet('allodos-recess', JSON.stringify(next));
    }
  };

  // Carrega a página publicada da clínica autenticada e adota o que ela traz.
  useEffect(() => {
    // A página publicada é da clínica: o profissional não a adota nem publica.
    if (!canPublish) return;
    let cancelado = false;
    fetchMyBookingPage()
      .then((page) => {
        if (cancelado || !page) return;
        setPublishedPage(page);
        adoptPublishedPage(page);
      })
      .catch((err) => console.error('Falha ao carregar a página de agendamento publicada:', err));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Bloco 36: carrega do servidor os horários do profissional escolhido no
   * filtro de "Configurar Horários". Sem horários salvos para ele, a grade
   * parte dos horários da página (grade padrão da clínica) ou do padrão.
   */
  const loadHoursFor = React.useCallback(async (userId: string) => {
    if (!userId) return;
    setHoursProfessionalLoading(true);
    try {
      const cfg = await fetchAgendaConfigFor(userId);
      const proprios = Array.isArray(cfg?.hours) && cfg!.hours.some((h) => h?.active && (h.slots?.length ?? 0) > 0)
        ? (cfg!.hours as unknown as DayHours[])
        : null;
      const daPagina = publishedPage?.config?.hours as unknown as DayHours[] | undefined;
      const base = proprios ?? (daPagina?.length ? daPagina : null) ?? EMPTY_HOURS;
      setHours(base);
      setSavedHoursJson(JSON.stringify(base));
    } catch (err) {
      console.error('Falha ao carregar os horários do profissional:', err);
    } finally {
      setHoursProfessionalLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedPage]);

  const switchHoursProfessional = async (userId: string) => {
    if (userId === hoursProfessionalId) return;
    if (hoursDirty && !window.confirm('Há horários não salvos. Trocar de profissional descarta essas alterações. Continuar?')) {
      return;
    }
    setHoursProfessionalId(userId);
    await loadHoursFor(userId);
  };

  // Ao abrir a tela (ou quando o perfil ativo muda), carrega os horários do
  // profissional selecionado — o próprio usuário por padrão.
  const hoursLoadedForRef = React.useRef<string>('');
  useEffect(() => {
    const alvo = hoursProfessionalId || String(activeProfile?.id ?? '');
    if (!alvo || hoursLoadedForRef.current === alvo) return;
    hoursLoadedForRef.current = alvo;
    if (!hoursProfessionalId) setHoursProfessionalId(alvo);
    void loadHoursFor(alvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoursProfessionalId, activeProfile?.id]);

  // Serviços, locais e profissionais refletem automaticamente no link externo
  // quando a página já está publicada (ex.: desmarcar um local some da tela
  // pública sem precisar republicar à mão). Horários só entram ao salvar.
  const syncReadyRef = React.useRef(false);
  useEffect(() => {
    if (!publishedPage || !canPublish) return;
    if (!syncReadyRef.current) {
      syncReadyRef.current = true;
      return;
    }
    const timer = window.setTimeout(() => {
      const savedHours = (() => {
        try {
          return JSON.parse(savedHoursJson) as DayHours[];
        } catch {
          return hours;
        }
      })();
      publishBookingPage({ slug: publishedPage.slug, config: buildBookingConfig(savedHours) })
        .then((page) => setPublishedPage(page))
        .catch((err) => console.error('Falha ao sincronizar a página de agendamento:', err));
    }, 800);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, locations, professionals, recess]);

  const handleOpenBookingPage = async () => {
    if (!slug || isPublishing) return;
    try {
      const page = await handlePublish();
      const url = `${window.location.origin}/?agendar=${encodeURIComponent(page.slug)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // handlePublish já mostra o erro na tela.
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(bookingLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleToggleServiceActive = (id: string) => {
    setServices(services.map(s => s.id === id ? { ...s, active: s.active === false ? true : false } : s));
  };

  const handleToggleLocationActive = (id: string) => {
    setLocations(locations.map(l => l.id === id ? { ...l, active: l.active === false ? true : false } : l));
  };

  const handleAddService = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName) return;

    if (editingService) {
      setServices(services.map(s => s.id === editingService.id ? {
        ...s,
        name: newServiceName,
        duration: newServiceDuration,
        price: newServicePrice,
        description: newServiceDesc,
        professionalIds: newServiceProfIds,
      } : s));
      setEditingService(null);
    } else {
      const newSvc: Service = {
        id: Date.now().toString(),
        name: newServiceName,
        duration: newServiceDuration,
        price: newServicePrice,
        description: newServiceDesc,
        active: true,
        professionalIds: newServiceProfIds,
      };
      setServices([...services, newSvc]);
    }

    setNewServiceName('');
    setNewServiceDuration(50);
    setNewServicePrice(150);
    setNewServiceDesc('');
    setNewServiceProfIds(allProfessionalIds);
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setNewServiceName('');
    setNewServiceDuration(50);
    setNewServicePrice(150);
    setNewServiceDesc('');
    setNewServiceProfIds(allProfessionalIds);
  };

  const handleRemoveService = (id: string) => {
    setServices(services.filter(s => s.id !== id));
  };

  const handleAddLocation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLocName) return;

    if (editingLocation) {
      setLocations(locations.map(l => l.id === editingLocation.id ? {
        ...l,
        name: newLocName,
        type: newLocType,
        address: newLocType === 'presential' ? newLocAddress : undefined,
        link: newLocType === 'online' ? newLocLink.trim() || undefined : undefined,
        professionalIds: newLocProfIds,
      } : l));
      setEditingLocation(null);
    } else {
      const newLoc: Location = {
        id: Date.now().toString(),
        name: newLocName,
        type: newLocType,
        address: newLocType === 'presential' ? newLocAddress : undefined,
        link: newLocType === 'online' ? newLocLink.trim() || undefined : undefined,
        active: true,
        professionalIds: newLocProfIds,
      };
      setLocations([...locations, newLoc]);
    }

    setNewLocName('');
    setNewLocType('online');
    setNewLocAddress('');
    setNewLocLink('');
    setNewLocProfIds(allProfessionalIds);
  };

  const handleCancelEditLocation = () => {
    setEditingLocation(null);
    setNewLocName('');
    setNewLocType('online');
    setNewLocAddress('');
    setNewLocLink('');
    setNewLocProfIds(allProfessionalIds);
  };

  const handleRemoveLocation = (id: string) => {
    setLocations(locations.filter(l => l.id !== id));
  };

  const handleToggleDay = (dayIndex: number) => {
    const updated = [...hours];
    updated[dayIndex].active = !updated[dayIndex].active;
    
    // If it was turned active and has no slots, let's add a default slot
    if (updated[dayIndex].active && (!updated[dayIndex].slots || updated[dayIndex].slots.length === 0)) {
      const defaultSlot: TimeSlot = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 7),
        start: '09:00',
        end: '13:00',
        modality: 'PRESENCIAL',
        locationId: locations[0]?.id || '',
        serviceIds: services[0] ? [services[0].id] : []
      };
      updated[dayIndex].slots = [defaultSlot];
    }
    setHours(updated);
  };

  const handleAddSlot = (dayIndex: number) => {
    const updated = [...hours];
    const defaultSlot: TimeSlot = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 7),
      start: '09:00',
      end: '13:00',
      modality: 'PRESENCIAL',
      locationId: locations[0]?.id || '',
      serviceIds: services[0] ? [services[0].id] : []
    };
    
    // Ensure the day is active if we add a slot
    updated[dayIndex].active = true;
    updated[dayIndex].slots = [...(updated[dayIndex].slots || []), defaultSlot];
    setHours(updated);
  };

  const handleRemoveSlot = (dayIndex: number, slotId: string) => {
    const updated = [...hours];
    updated[dayIndex].slots = updated[dayIndex].slots.filter(s => s.id !== slotId);
    // If no slots left, set day to inactive
    if (updated[dayIndex].slots.length === 0) {
      updated[dayIndex].active = false;
    }
    setHours(updated);
  };

  const handleUpdateSlot = (dayIndex: number, slotId: string, updates: Partial<TimeSlot>) => {
    const updated = [...hours];
    updated[dayIndex].slots = updated[dayIndex].slots.map(slot => 
      slot.id === slotId ? { ...slot, ...updates } : slot
    );
    setHours(updated);
  };

  const handleCopySlots = (sourceIndex: number) => {
    const updated = [...hours];
    const sourceDay = updated[sourceIndex];
    
    updated.forEach((dayObj, idx) => {
      if (copyTargetDays[dayObj.day] && idx !== sourceIndex) {
        // Deep copy the slots with new ids to prevent reference issues
        dayObj.slots = (sourceDay.slots || []).map(slot => ({
          ...slot,
          id: Date.now().toString() + Math.random().toString(36).substring(2, 7)
        }));
        dayObj.active = sourceDay.active;
      }
    });
    
    setHours(updated);
    setCopySourceDayIndex(null);
    setCopyTargetDays({});
  };

  const switchTab = (tab: typeof activeTab) => {
    if (tab !== 'hours' && activeTab === 'hours' && hoursDirty) {
      const sair = window.confirm('Há alterações não salvas em Configurar Horários. Sair sem salvar?');
      if (!sair) return;
    }
    setActiveTab(tab);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Dynamic Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">Configurações da Agenda</h3>
          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold mt-1">Gerencie como seus pacientes agendam consultas com você.</p>
        </div>
      </div>

      {/* Tabs navigation resembling the patient details profile header tabs */}
      <div className="w-full overflow-x-auto pb-2 -mb-2 no-scrollbar">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 p-1.5 rounded-3xl flex flex-nowrap lg:flex-wrap gap-2 border border-slate-200/50 dark:border-slate-800/50 shadow-inner w-max lg:w-auto">
          <button 
            onClick={() => switchTab('services')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 shrink-0 ${
              activeTab === 'services' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 scale-[1.02]' 
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/60'
            }`}
          >
            <Briefcase size={14} />
            Serviços Oferecidos
          </button>
          
          <button 
            onClick={() => switchTab('locations')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 shrink-0 ${
              activeTab === 'locations' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 scale-[1.02]' 
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/60'
            }`}
          >
            <MapPin size={14} />
            Locais de Atendimento
          </button>

          <button 
            onClick={() => switchTab('hours')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 shrink-0 ${
              activeTab === 'hours' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 scale-[1.02]' 
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/60'
            }`}
          >
            <Clock size={14} />
            Configurar Horários
          </button>

          {canPublish && (
          <button 
            onClick={() => switchTab('booking-link')}
            className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 shrink-0 ${
              activeTab === 'booking-link' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 scale-[1.02]' 
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/60'
            }`}
          >
            <LinkIcon size={14} />
            Configuração página de agendamento
          </button>
          )}
        </div>
      </div>

      {/* Tab Panels */}
      <div className={activeTab === 'booking-link' ? "w-full" : "grid grid-cols-1 lg:grid-cols-3 gap-8 items-start"}>
        
        {/* Main Content Areas (takes 2 columns or full-width) */}
        <div className={activeTab === 'booking-link' ? "w-full space-y-8" : "lg:col-span-2 space-y-6"}>
          
          {/* SERVICES PANEL */}
          {activeTab === 'services' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((svc) => (
                  <div 
                    key={svc.id} 
                    className={`bg-white dark:bg-slate-900 border p-6 rounded-[2.5rem] shadow-sm relative group hover:border-indigo-400 dark:hover:border-indigo-800 transition-all flex flex-col justify-between ${
                      svc.active !== false 
                        ? 'border-slate-200 dark:border-slate-800' 
                        : 'border-slate-200/60 dark:border-slate-800/60 opacity-60 border-dashed'
                    }`}
                  >
                    <div className="absolute top-6 right-6 flex items-center gap-2">
                      {/* Checkbox (caixa de seleção) */}
                      <input 
                        type="checkbox"
                        checked={svc.active !== false}
                        onChange={() => handleToggleServiceActive(svc.id)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-pointer"
                        title={svc.active !== false ? "Desativar Serviço" : "Ativar Serviço"}
                      />

                      {/* Pencil Icon (Lápis) */}
                      <button 
                        onClick={() => {
                          setEditingService(svc);
                          setNewServiceName(svc.name);
                          setNewServiceDuration(svc.duration);
                          setNewServicePrice(svc.price);
                          setNewServiceDesc(svc.description);
                          setNewServiceProfIds(svc.professionalIds?.length ? svc.professionalIds : allProfessionalIds);
                        }}
                        className="p-2 text-slate-400 hover:text-indigo-600 bg-slate-50 dark:bg-slate-800 rounded-xl hover:scale-105 transition-all cursor-pointer"
                        title="Editar Serviço"
                      >
                        <Pencil size={14} />
                      </button>

                      {/* Trash Icon (Lixeira) */}
                      <button 
                        onClick={() => handleRemoveService(svc.id)}
                        className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 dark:bg-slate-800 rounded-xl hover:scale-105 transition-all cursor-pointer"
                        title="Excluir Serviço"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400">
                          <Briefcase size={16} />
                          <span className="text-[10px] font-black uppercase tracking-widest">{svc.duration} minutos</span>
                        </div>
                        {svc.active === false && (
                          <span className="px-2.5 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 text-[8px] font-black uppercase tracking-widest rounded-md">
                            Inativo
                          </span>
                        )}
                      </div>
                      <h4 className="text-base font-black text-slate-800 dark:text-slate-100 tracking-tight mb-2 pr-24">{svc.name}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold leading-relaxed mb-4">{svc.description}</p>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
                        Profissionais: <span className="text-slate-600 dark:text-slate-300">{professionalNamesFor(svc.professionalIds)}</span>
                      </p>
                    </div>
                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center mt-auto">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Preço Sugerido</span>
                      <span className="text-lg font-black text-slate-800 dark:text-slate-100">R$ {svc.price},00</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LOCATIONS PANEL */}
          {activeTab === 'locations' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {locations.map((loc) => (
                  <div 
                    key={loc.id} 
                    className={`bg-white dark:bg-slate-900 border p-6 rounded-[2.5rem] shadow-sm relative group hover:border-indigo-400 dark:hover:border-indigo-800 transition-all flex flex-col justify-between ${
                      loc.active !== false 
                        ? 'border-slate-200 dark:border-slate-800' 
                        : 'border-slate-200/60 dark:border-slate-800/60 opacity-60 border-dashed'
                    }`}
                  >
                    <div className="absolute top-6 right-6 flex items-center gap-2">
                      {/* Checkbox (caixa de seleção) */}
                      <input 
                        type="checkbox"
                        checked={loc.active !== false}
                        onChange={() => handleToggleLocationActive(loc.id)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-pointer"
                        title={loc.active !== false ? "Desativar Local" : "Ativar Local"}
                      />

                      {/* Pencil Icon (Lápis) */}
                      <button 
                        onClick={() => {
                          setEditingLocation(loc);
                          setNewLocName(loc.name);
                          setNewLocType(loc.type);
                          setNewLocAddress(loc.address || '');
                          setNewLocLink(loc.link || '');
                          setNewLocProfIds(loc.professionalIds?.length ? loc.professionalIds : allProfessionalIds);
                        }}
                        className="p-2 text-slate-400 hover:text-indigo-600 bg-slate-50 dark:bg-slate-800 rounded-xl hover:scale-105 transition-all cursor-pointer"
                        title="Editar Local"
                      >
                        <Pencil size={14} />
                      </button>

                      {/* Trash Icon (Lixeira) */}
                      <button 
                        onClick={() => handleRemoveLocation(loc.id)}
                        className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 dark:bg-slate-800 rounded-xl hover:scale-105 transition-all cursor-pointer"
                        title="Excluir Local"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {loc.type === 'online' ? (
                          <div className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center gap-1.5">
                            <Globe size={10} /> Online
                          </div>
                        ) : (
                          <div className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center gap-1.5">
                            <MapPin size={10} /> Presencial
                          </div>
                        )}
                        {loc.active === false && (
                          <span className="px-2.5 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 text-[8px] font-black uppercase tracking-widest rounded-md">
                            Inativo
                          </span>
                        )}
                      </div>
                      <h4 className="text-base font-black text-slate-800 dark:text-slate-100 tracking-tight mb-2 pr-24">{loc.name}</h4>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                        Profissionais: <span className="text-slate-600 dark:text-slate-300">{professionalNamesFor(loc.professionalIds)}</span>
                      </p>
                      {loc.type === 'online' ? (
                        <div className="mb-4 space-y-2">
                          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold leading-relaxed">
                            Para um bom atendimento, garanta uma boa conexão de internet e privacidade. Certifique-se acerca das permissões de câmera e microfone.
                          </p>
                          {/* Bloco 57: sem o link, "Acessar Sala" não consegue avisar o paciente. */}
                          {loc.link ? (
                            <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 break-all flex items-start gap-1.5">
                              <Globe size={12} className="shrink-0 mt-0.5" /> Sala: <a href={loc.link} target="_blank" rel="noreferrer" className="underline">{loc.link}</a>
                            </p>
                          ) : (
                            <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 px-3 py-2 rounded-xl">
                              Sem link da sala virtual: ao clicar em "Acessar Sala" o paciente não receberá o aviso. Edite o local e informe o link do Google Meet.
                            </p>
                          )}
                        </div>
                      ) : (
                        loc.address && (
                          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold leading-relaxed mb-4 flex items-start gap-2">
                            <MapPin size={14} className="shrink-0 mt-0.5 text-slate-400" />
                            {loc.address}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HOURS PANEL */}
          {activeTab === 'hours' && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6 sm:space-y-8">
              <div className="border-b border-slate-100 dark:border-slate-800 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h4 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">Horários de Atendimento</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold mt-1">Cadastre a disponibilidade, locais e serviços de cada profissional para criar a agenda dele.</p>
                </div>
                {/* Bloco 36: de quem são os horários exibidos */}
                <div className="space-y-1.5 md:min-w-[280px]">
                  <label htmlFor="hours-professional" className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Horários de</label>
                  <select
                    id="hours-professional"
                    value={hoursProfessionalId}
                    onChange={(e) => void switchHoursProfessional(e.target.value)}
                    disabled={hoursProfessionalLoading}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer disabled:opacity-60"
                  >
                    {sortedProfs.map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.name}{String(p.id) === String(activeProfile?.id ?? '') ? ' (você)' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {hours.map((item, index) => (
                  <div key={item.day} className="pb-6 border-b border-slate-100 dark:border-slate-800 last:border-0 last:pb-0 space-y-4">
                    {/* Day Header Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={item.active} 
                            onChange={() => handleToggleDay(index)} 
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 dark:bg-slate-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-indigo-600"></div>
                        </label>
                        <span className={`text-sm font-black tracking-tight uppercase ${item.active ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-600'}`}>
                          {item.day.split('-')[0]}
                        </span>
                      </div>

                      {/* Day Header Buttons (Add slot & Copy formatting) */}
                      <div className="flex items-center gap-2 relative">
                        <button 
                          type="button"
                          onClick={() => handleAddSlot(index)}
                          className="p-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 rounded-lg transition-all cursor-pointer flex items-center justify-center"
                          title="Adicionar Faixa de Horário"
                        >
                          <Plus size={14} />
                        </button>

                        <button 
                          type="button"
                          onClick={() => {
                            setCopySourceDayIndex(index);
                            const targets: {[key: string]: boolean} = {};
                            hours.forEach(d => {
                              targets[d.day] = false;
                            });
                            setCopyTargetDays(targets);
                          }}
                          className="p-1.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 hover:text-indigo-600 rounded-lg transition-all cursor-pointer flex items-center justify-center"
                          title="Copiar para outros dias"
                        >
                          <Copy size={14} />
                        </button>

                        {/* Copy Popover Overlay */}
                        {copySourceDayIndex === index && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setCopySourceDayIndex(null)}></div>
                            <div className="absolute top-10 right-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-3xl shadow-2xl z-50 w-56 space-y-4 text-left">
                              <div className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-2">Copiar para</div>
                              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {hours.map((dayObj, dIdx) => (
                                  <label 
                                    key={dayObj.day} 
                                    className={`flex items-center gap-2.5 text-xs font-bold cursor-pointer py-1.5 px-2 rounded-xl transition-all ${
                                      dIdx === index 
                                        ? 'opacity-40 cursor-not-allowed bg-slate-50 dark:bg-slate-950/20 text-slate-400' 
                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                                    }`}
                                  >
                                    <input 
                                      type="checkbox"
                                      disabled={dIdx === index}
                                      checked={dIdx === index ? false : !!copyTargetDays[dayObj.day]}
                                      onChange={(e) => {
                                        setCopyTargetDays({
                                          ...copyTargetDays,
                                          [dayObj.day]: e.target.checked
                                        });
                                      }}
                                      className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 cursor-pointer"
                                    />
                                    <span className="uppercase">{dayObj.day.split('-')[0]}</span>
                                  </label>
                                ))}
                              </div>
                              <button 
                                type="button"
                                onClick={() => handleCopySlots(index)}
                                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-md shadow-indigo-600/10"
                              >
                                <Check size={12} /> Salvar
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Time Slots (Faixas de Horários) list */}
                    {item.active && item.slots && item.slots.length > 0 ? (
                      <div className="space-y-4 pl-4 border-l-2 border-slate-100 dark:border-slate-800 ml-5">
                        {item.slots.map((slot) => (
                          <div key={slot.id} className="space-y-3 bg-slate-50/50 dark:bg-slate-950/20 border border-slate-100/80 dark:border-slate-900/80 p-5 rounded-[2rem] relative">
                            {/* Row 1: Time range & delete button */}
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                                <span className="text-[10px] font-black uppercase text-slate-400 shrink-0">Das</span>
                                <div className="relative flex-1 min-w-[112px]">
                                  <input 
                                    type="time" 
                                    value={slot.start}
                                    onChange={(e) => handleUpdateSlot(index, slot.id, { start: e.target.value })}
                                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 pl-8 pr-2 py-2 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-black cursor-pointer"
                                  />
                                  <Clock size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                                
                                <span className="text-[10px] font-black uppercase text-slate-400 shrink-0">Às</span>
                                <div className="relative flex-1 min-w-[112px]">
                                  <input 
                                    type="time" 
                                    value={slot.end}
                                    onChange={(e) => handleUpdateSlot(index, slot.id, { end: e.target.value })}
                                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 pl-8 pr-2 py-2 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-black cursor-pointer"
                                  />
                                  <Clock size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                              </div>

                              <button 
                                type="button"
                                onClick={() => handleRemoveSlot(index, slot.id)}
                                className="p-2 text-slate-400 hover:text-red-500 bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-xl hover:scale-105 transition-all cursor-pointer shrink-0"
                                title="Excluir Faixa de Horário"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                            {/* Row 2: Modality and Location selectors (bloco #67: + Presencial & Online) */}
                            {(() => {
                              const ativos = locations.filter(l => l.active !== false);
                              const presenciais = ativos.filter(l => l.type === 'presential');
                              const onlines = ativos.filter(l => l.type === 'online');
                              const hibrida = slot.modality === 'PRESENCIAL_ONLINE';
                              // Mantém visível o local já escolhido mesmo se for de outra modalidade (faixas antigas).
                              const opcoes = (lista: typeof ativos, atual?: string) => {
                                const extra = atual && !lista.some(l => l.id === atual) ? ativos.filter(l => l.id === atual) : [];
                                return [...lista, ...extra];
                              };
                              const trocarModalidade = (modality: TimeSlot['modality']) => {
                                const atual = ativos.find(l => l.id === slot.locationId);
                                if (modality === 'PRESENCIAL_ONLINE') {
                                  handleUpdateSlot(index, slot.id, {
                                    modality,
                                    locationId: atual?.type === 'presential' ? atual.id : presenciais[0]?.id || '',
                                    onlineLocationId: slot.onlineLocationId || (atual?.type === 'online' ? atual.id : onlines[0]?.id || ''),
                                  });
                                  return;
                                }
                                const doTipo = modality === 'ONLINE' ? onlines : presenciais;
                                const fica = atual && doTipo.some(l => l.id === atual.id);
                                handleUpdateSlot(index, slot.id, {
                                  modality,
                                  locationId: fica ? atual!.id : (modality === 'ONLINE' ? slot.onlineLocationId : '') || doTipo[0]?.id || '',
                                  onlineLocationId: undefined,
                                });
                              };
                              const selectCls = "w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 pl-4 pr-10 py-2.5 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-bold cursor-pointer appearance-none";
                              const chevron = (
                                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                  <ChevronDown size={14} />
                                </div>
                              );
                              const listaUnica = slot.modality === 'ONLINE' ? onlines : presenciais;
                              return (
                                <div className="space-y-2">
                                  <div className={`grid grid-cols-1 ${hibrida ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3`}>
                                    <div className="relative">
                                      <select
                                        value={slot.modality}
                                        onChange={(e) => trocarModalidade(e.target.value as TimeSlot['modality'])}
                                        aria-label="Modalidade da faixa"
                                        className={selectCls}
                                      >
                                        <option value="PRESENCIAL">PRESENCIAL</option>
                                        <option value="ONLINE">ONLINE</option>
                                        <option value="PRESENCIAL_ONLINE">PRESENCIAL &amp; ONLINE</option>
                                      </select>
                                      {chevron}
                                    </div>

                                    {hibrida ? (
                                      <>
                                        <div className="relative">
                                          <select
                                            value={slot.locationId}
                                            onChange={(e) => handleUpdateSlot(index, slot.id, { locationId: e.target.value })}
                                            aria-label="Local presencial da faixa"
                                            className={selectCls}
                                          >
                                            <option value="">{presenciais.length ? 'Qualquer local presencial' : 'Nenhum local presencial cadastrado'}</option>
                                            {opcoes(presenciais, slot.locationId).map(loc => (
                                              <option key={loc.id} value={loc.id}>{loc.name} (presencial)</option>
                                            ))}
                                          </select>
                                          {chevron}
                                        </div>
                                        <div className="relative">
                                          <select
                                            value={slot.onlineLocationId || ''}
                                            onChange={(e) => handleUpdateSlot(index, slot.id, { onlineLocationId: e.target.value })}
                                            aria-label="Teleconsulta da faixa"
                                            className={selectCls}
                                          >
                                            <option value="">{onlines.length ? 'Qualquer teleconsulta' : 'Nenhuma teleconsulta cadastrada'}</option>
                                            {opcoes(onlines, slot.onlineLocationId).map(loc => (
                                              <option key={loc.id} value={loc.id}>{loc.name} (online)</option>
                                            ))}
                                          </select>
                                          {chevron}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="relative">
                                        <select
                                          value={slot.locationId}
                                          onChange={(e) => handleUpdateSlot(index, slot.id, { locationId: e.target.value })}
                                          aria-label="Local da faixa"
                                          className={selectCls}
                                        >
                                          <option value="">Selecione o local...</option>
                                          {opcoes(listaUnica, slot.locationId).map(loc => (
                                            <option key={loc.id} value={loc.id}>
                                              {loc.name} {loc.type === 'presential' ? '(presencial)' : '(online)'}
                                            </option>
                                          ))}
                                        </select>
                                        {chevron}
                                      </div>
                                    )}
                                  </div>
                                  {hibrida && (!presenciais.length || !onlines.length) && (
                                    <p className="text-[10px] font-bold text-amber-600 ml-1">
                                      Cadastre {!presenciais.length ? 'um local presencial' : 'uma teleconsulta'} em Locais de Atendimento: sem ele, o link externo oferece esta faixa só {!presenciais.length ? 'online' : 'presencial'}.
                                    </p>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Row 3: Services selector */}
                            <div className="relative">
                              <div 
                                className="min-h-[42px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2 flex flex-wrap gap-2 items-center justify-between cursor-pointer" 
                                onClick={() => setOpenServiceDropdownId(openServiceDropdownId === slot.id ? null : slot.id)}
                              >
                                <div className="flex flex-wrap gap-2">
                                  {(slot.serviceIds || []).map(id => {
                                    const svc = services.find(s => s.id === id);
                                    if (!svc) return null;
                                    return (
                                      <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-wider rounded-lg border border-indigo-100/50 dark:border-indigo-900/50">
                                        {svc.name}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newIds = slot.serviceIds.filter(sid => sid !== id);
                                            handleUpdateSlot(index, slot.id, { serviceIds: newIds });
                                          }}
                                          className="hover:bg-indigo-200 dark:hover:bg-indigo-900 rounded p-0.5 text-indigo-500 hover:text-indigo-700 transition-colors"
                                        >
                                          &times;
                                        </button>
                                      </span>
                                    );
                                  })}
                                  {(!slot.serviceIds || slot.serviceIds.length === 0) && (
                                    <span className="text-slate-400 text-xs font-medium">Selecione os serviços...</span>
                                  )}
                                </div>
                                <div className="text-slate-400 shrink-0">
                                  <ChevronDown size={14} />
                                </div>
                              </div>
                              
                              {/* Dropdown list of services */}
                              {openServiceDropdownId === slot.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenServiceDropdownId(null)}></div>
                                  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto p-1.5 space-y-1 text-left">
                                    {services.map(svc => {
                                      const isSelected = (slot.serviceIds || []).includes(svc.id);
                                      return (
                                        <button
                                          key={svc.id}
                                          type="button"
                                          onClick={() => {
                                            const currentIds = slot.serviceIds || [];
                                            const newIds = isSelected 
                                              ? currentIds.filter(id => id !== svc.id)
                                              : [...currentIds, svc.id];
                                            handleUpdateSlot(index, slot.id, { serviceIds: newIds });
                                          }}
                                          className={`w-full text-left px-4 py-2.5 text-xs rounded-lg transition-all flex items-center justify-between ${
                                            isSelected 
                                              ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-bold' 
                                              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                                          }`}
                                        >
                                          <span>{svc.name}</span>
                                          {isSelected && <Check size={12} className="text-indigo-600 dark:text-indigo-400" />}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 bg-slate-50/50 dark:bg-slate-950/20 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl ml-5">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Fechado</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Rodapé fixo ao término da lista: salvar horários */}
              <div className="sticky bottom-0 -mx-4 sm:-mx-8 -mb-4 sm:-mb-8 px-4 sm:px-8 py-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-t border-slate-100 dark:border-slate-800 rounded-b-[1.5rem] sm:rounded-b-[2.5rem] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  {hoursFeedback ? (
                    <p className={`text-[11px] font-bold ${hoursFeedback.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                      {hoursFeedback.msg}
                    </p>
                  ) : hoursDirty ? (
                    <p className="text-[11px] font-bold text-amber-700 dark:text-amber-300">
                      Há alterações não salvas nos horários.
                    </p>
                  ) : (
                    <p className="text-[11px] font-semibold text-slate-400">
                      {!canPublish
                        ? 'Seus horários ficam salvos no seu perfil e valem no link de agendamento da clínica quando o paciente escolhe você.'
                        : publishedPage
                          ? 'Ao salvar, os horários do link de agendamento são atualizados automaticamente.'
                          : 'Salve e depois publique a página para ofertar estes horários no link externo.'}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSaveHours}
                  disabled={isSavingHours}
                  className="px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-600/15 shrink-0"
                >
                  {isSavingHours ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Salvar Alterações
                </button>
              </div>
            </div>
          )}

          {/* BOOKING LINK PANEL */}
          {activeTab === 'booking-link' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
              {/* TOP HEADER SECTION */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-6 rounded-3xl shadow-sm space-y-4 animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-2xl text-indigo-600 dark:text-indigo-400">
                      <Shield size={22} />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-800 dark:text-slate-100 tracking-tight">Configuração da Página de Agendamento</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold mt-0.5">Customize o portal onde seus pacientes agendam as consultas online e visualize em tempo real.</p>
                    </div>
                  </div>
                  
                  {publishedPage ? (
                    <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/20 px-3.5 py-1.5 rounded-xl border border-emerald-100 dark:border-emerald-900/40 w-fit">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Página Pública Ativa</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/20 px-3.5 py-1.5 rounded-xl border border-amber-100 dark:border-amber-900/40 w-fit">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">Página ainda não publicada</span>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 space-y-3">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Link de Acesso Público</label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 sm:px-5 py-3 sm:py-3.5 rounded-2xl flex items-center overflow-hidden">
                      <span className="text-[10px] sm:text-xs font-semibold text-slate-400 dark:text-slate-500 select-none shrink-0">{bookingLinkPrefix}</span>
                      <input
                        type="text"
                        value={slug}
                        onChange={(e) => {
                          const cleanVal = e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '');
                          setSlug(cleanVal);
                          scopedSet('allodos-booking-slug', cleanVal);
                        }}
                        className="flex-1 min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-0 ml-1 text-xs sm:text-sm font-black text-slate-800 dark:text-slate-100 font-sans"
                        placeholder="seu-nome"
                      />
                      <LinkIcon size={14} className="text-slate-400 shrink-0 ml-2" />
                    </div>
                    <button 
                      onClick={handleCopyLink}
                      className="px-6 py-3.5 sm:py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-indigo-600/10 shrink-0"
                    >
                      {copied ? (
                        <>
                          <Check size={14} /> Copiado!
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Copiar Link
                        </>
                      )}
                    </button>
                  </div>

                  {/* Publicar: leva a configuração desta tela para a API, que é
                      o que torna a página acessível a qualquer paciente. */}
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      onClick={handlePublish}
                      disabled={isPublishing || !slug}
                      className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shrink-0"
                    >
                      {isPublishing ? (
                        <><Loader2 size={14} className="animate-spin" /> Publicando...</>
                      ) : (
                        <><Globe size={14} /> Publicar Página</>
                      )}
                    </button>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                      Publique para que os pacientes vejam seus serviços, horários e profissionais.
                      Republique sempre que alterar algo aqui.
                    </p>
                  </div>

                  {publishFeedback && (
                    <p
                      className={`mt-3 text-[11px] font-bold p-3.5 rounded-2xl ${
                        publishFeedback.ok
                          ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300'
                          : 'text-rose-700 bg-rose-50 dark:bg-rose-950/30 dark:text-rose-300'
                      }`}
                    >
                      {publishFeedback.msg}
                    </p>
                  )}
                </div>
              </div>

              {/* CONFIGURAÇÕES AVANÇADAS DE FLUXO (HORIZONTALIZADA E DE PONTA A PONTA) */}
              {/* Bloco #64: Modo Recesso / Férias */}
              <div className={`border p-5 rounded-3xl shadow-sm animate-in fade-in duration-300 space-y-5 ${recess.enabled ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/50' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Modo Recesso / Férias</h5>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mt-0.5">
                      Com o modo ativo, o link externo esconde os horários e exibe um formulário de lista de espera; cada inscrito recebe o acolhimento no WhatsApp.
                    </p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer shrink-0 bg-slate-50 dark:bg-slate-800/40 px-4 py-2.5 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{recess.enabled ? 'Recesso ativo' : 'Ativar Modo Recesso / Férias'}</span>
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label="Ativar Modo Recesso / Férias"
                      checked={!!recess.enabled}
                      onChange={(e) => updateRecess({ enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:bg-amber-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 relative" />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">Data prevista de retorno</label>
                    <input
                      type="date"
                      value={recess.returnDate || ''}
                      onChange={(e) => updateRecess({ returnDate: e.target.value })}
                      className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-bold text-slate-700 dark:text-slate-200 outline-none"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">Texto de aviso (mostrado no link externo)</label>
                    <textarea
                      rows={2}
                      value={recess.notice || ''}
                      onChange={(e) => updateRecess({ notice: e.target.value })}
                      placeholder="Ex.: Estamos em recesso acadêmico. Deixe seus dados e entraremos em contato na retomada das triagens."
                      className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-semibold text-slate-700 dark:text-slate-200 outline-none resize-none"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">
                    Mensagem de confirmação no WhatsApp — variáveis: {'{Nome}'}, {'{Nome da Clínica}'}, {'{Data de Retorno}'}
                  </label>
                  <textarea
                    rows={4}
                    value={recess.whatsappTemplate || ''}
                    onChange={(e) => updateRecess({ whatsappTemplate: e.target.value })}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-semibold text-slate-700 dark:text-slate-200 outline-none resize-none"
                  />
                  <button type="button" onClick={() => updateRecess({ whatsappTemplate: DEFAULT_RECESS_WHATSAPP_TEMPLATE })} className="text-[9px] font-black uppercase tracking-widest text-indigo-500 hover:underline">
                    Restaurar texto padrão (com orientação CVV 188)
                  </button>
                </div>
                {recess.enabled && !publishedPage && (
                  <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300">Publique a página para o recesso valer no link externo.</p>
                )}
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 rounded-3xl shadow-sm animate-in fade-in duration-300">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  {/* Esquerda: Titulo e Toggle */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                    <div>
                      <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Configurações Avançadas de Fluxo</h5>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mt-0.5">Defina as regras de funcionamento da sua agenda online.</p>
                    </div>
                    <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/40 px-4 py-2.5 rounded-2xl border border-slate-100 dark:border-slate-800/60">
                      <div className="space-y-0.5">
                        <h6 className="text-[11px] font-bold text-slate-700 dark:text-slate-200">Confirmar automaticamente</h6>
                        <p className="text-[9px] text-slate-400">Marcar como confirmado direto no sistema.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none ml-4 shrink-0">
                        <input 
                          type="checkbox" 
                          checked={autoBooking}
                          onChange={(e) => {
                            setAutoBooking(e.target.checked);
                            scopedSet('allodos-auto-booking', e.target.checked ? 'true' : 'false');
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-focus:ring-1 peer-focus:ring-indigo-500/20 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Direita: Campos de Janela, Intervalo, Antecedência */}
                  <div className="grid grid-cols-3 gap-3 shrink-0 lg:max-w-md w-full">
                    <div className="space-y-1">
                      <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">Janela (Dias)</label>
                      <input 
                        type="number" 
                        value={openDaysLimit}
                        onChange={(e) => {
                          const val = Math.max(0, parseInt(e.target.value, 10) || 0);
                          setOpenDaysLimit(val);
                          scopedSet('allodos-open-days-limit', val.toString());
                        }}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">Intervalo (Min)</label>
                      <input 
                        type="number" 
                        value={minInterval}
                        onChange={(e) => {
                          const val = Math.max(1, parseInt(e.target.value, 10) || 0);
                          setMinInterval(val);
                          scopedSet('allodos-min-interval', val.toString());
                        }}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1">Anteced. (Horas)</label>
                      <input 
                        type="number" 
                        value={minAdvanceHours}
                        onChange={(e) => {
                          const val = Math.max(0, parseInt(e.target.value, 10) || 0);
                          setMinAdvanceHours(val);
                          scopedSet('allodos-min-advance-hours', val.toString());
                        }}
                        className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* MAIN CONTENT WORKSPACE SPLIT: GRID PARA PROFISSIONAIS E LINKS */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  
                  {/* PROFESSIONAL MANAGER SECTION */}
                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm space-y-5">
                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-indigo-500" />
                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Profissionais da Clínica</h4>
                      </div>
                    </div>

                    {/* Add / Edit Professional Form */}
                    {showProfessionalForm && (
                      <form 
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!profFormation) return;
                          
                          let updatedList: Professional[];
                          if (editingProf) {
                            updatedList = professionals.map(p => p.id === editingProf.id ? {
                              ...p,
                              formation: profFormation,
                              bio: profBio
                            } : p);
                          } else {
                            const newProf: Professional = {
                              id: Date.now().toString(),
                              name: profName,
                              formation: profFormation,
                              crp: profCrp,
                              photo: profPhoto,
                              bio: profBio,
                              active: true
                            };
                            updatedList = [...professionals, newProf];
                          }
                          
                          setProfessionals(updatedList);
                          scopedSet('allodos-professionals', JSON.stringify(updatedList));
                          setShowProfessionalForm(false);
                          setEditingProf(null);
                        }}
                        className="p-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-4 animate-in fade-in duration-200"
                      >
                        <h5 className="text-xs font-black text-slate-800 dark:text-slate-100">
                          Editar Profissional
                        </h5>
                        
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block ml-1">Nome Completo</label>
                            <input 
                              type="text"
                              value={profName}
                              readOnly
                              disabled
                              className="w-full p-2.5 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-500 dark:text-slate-400 outline-none cursor-not-allowed"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block ml-1">Formação / Especialidade</label>
                            <input 
                              type="text"
                              value={profFormation}
                              onChange={(e) => setProfFormation(e.target.value)}
                              placeholder="Ex: Psicóloga Clínica (TCC)"
                              required
                              className="w-full p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block ml-1">CRP (Registro)</label>
                            <input 
                              type="text"
                              value={profCrp}
                              readOnly
                              disabled
                              className="w-full p-2.5 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-500 dark:text-slate-400 outline-none cursor-not-allowed"
                            />
                          </div>

                          <div className="space-y-1">
                            <div className="flex justify-between items-center ml-1">
                              <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Biografia / Perfil (Máx 240 caracteres)</label>
                              <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500">
                                {profBio.length}/240
                              </span>
                            </div>
                            <textarea 
                              value={profBio}
                              onChange={(e) => setProfBio(e.target.value.slice(0, 240))}
                              placeholder="Fale um pouco sobre sua atuação, abordagem e experiência..."
                              rows={3}
                              className="w-full p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none resize-none"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowProfessionalForm(false);
                              setEditingProf(null);
                            }}
                            className="px-3.5 py-2 text-slate-500 hover:bg-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer shadow-md shadow-indigo-600/10"
                          >
                            Salvar Profissional
                          </button>
                        </div>
                      </form>
                    )}

                     {/* Professionals list rendered for management */}
                    <div className="space-y-2.5">
                      {sortedProfs.map((prof) => (
                        <div 
                          key={prof.id} 
                          className="group relative flex items-center justify-between p-3 border border-slate-200 dark:border-slate-800 rounded-2xl hover:border-slate-300 dark:hover:border-slate-700 transition-all bg-slate-50/50 dark:bg-slate-900/40"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <ProfessionalAvatar name={prof.name} photo={prof.photo} className="w-9 h-9 rounded-xl border border-slate-100 dark:border-slate-800" />
                            <div className="min-w-0">
                              <h5 className="text-[11px] font-black text-slate-800 dark:text-slate-200 truncate">{prof.name}</h5>
                              <p className="text-[9px] text-slate-400 font-semibold truncate leading-tight">{prof.formation}</p>
                              <p className="text-[9px] text-slate-500 font-bold font-mono tracking-wide">{prof.crp}</p>
                              {prof.bio && (
                                <p className="text-[9px] text-slate-400 dark:text-slate-500 font-medium italic mt-1 line-clamp-1 max-w-[220px]">
                                  "{prof.bio}"
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Biography Hover Popover */}
                          {prof.bio && (
                            <div className="absolute right-2 top-full mt-1.5 w-64 p-3.5 bg-slate-900 dark:bg-slate-950 border border-slate-700/60 dark:border-slate-800 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none text-white">
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-1.5 pb-1 border-b border-slate-800">
                                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400"></div>
                                  <span className="text-[9px] font-black uppercase tracking-widest text-indigo-300">Biografia Completa</span>
                                </div>
                                <p className="text-[10px] text-slate-300 font-medium leading-relaxed whitespace-pre-wrap">
                                  {prof.bio}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            {/* Ativar/desativar a exibição na página de agendamento. */}
                            <div className="hidden xl:flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-2.5 py-1.5">
                              <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Exibir na página</span>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={prof.active !== false}
                                onClick={() => toggleProfessionalActive(prof.id)}
                                title={prof.active !== false ? 'Ocultar da página de agendamento' : 'Exibir na página de agendamento'}
                                className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${prof.active !== false ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                              >
                                <span
                                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prof.active !== false ? 'translate-x-4' : ''}`}
                                />
                              </button>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-label={prof.active !== false ? 'Ocultar da página de agendamento' : 'Exibir na página de agendamento'}
                              aria-checked={prof.active !== false}
                              onClick={() => toggleProfessionalActive(prof.id)}
                              title={prof.active !== false ? 'Ocultar da página de agendamento' : 'Exibir na página de agendamento'}
                              className={`xl:hidden relative w-9 h-5 rounded-full transition-colors shrink-0 ${prof.active !== false ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prof.active !== false ? 'translate-x-4' : ''}`}
                              />
                            </button>
                            <button
                              onClick={() => {
                                setEditingProf(prof);
                                setProfName(prof.name);
                                setProfFormation(prof.formation);
                                setProfCrp(prof.crp);
                                setProfPhoto(prof.photo);
                                setProfBio(prof.bio || '');
                                setShowProfessionalForm(true);
                              }}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg transition-all cursor-pointer"
                              title="Editar"
                            >
                              <Pencil size={11} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CUSTOM LINKS MANAGEMENT CARD */}
                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm space-y-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3">
                      <div className="flex items-center gap-2">
                        <LinkIcon size={16} className="text-indigo-500" />
                        <h5 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Links Complementares da Página</h5>
                      </div>
                      {!showAddCustomLink && (
                        <button 
                          onClick={() => setShowAddCustomLink(true)}
                          className="px-4 py-2 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900 text-indigo-600 dark:text-indigo-400 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all cursor-pointer w-full sm:w-auto"
                        >
                          <Plus size={12} /> Adicionar Link Personalizado
                        </button>
                      )}
                    </div>

                    {/* Add Custom Link Form */}
                    {showAddCustomLink && (
                      <div className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-4 animate-in fade-in duration-200">
                        <h6 className="text-xs font-black text-slate-800 dark:text-slate-100">Novo Link Personalizado</h6>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block ml-1">Título do Link</label>
                            <input 
                              type="text"
                              placeholder="Ex: Instituto Serena"
                              value={newCustomLinkName}
                              onChange={(e) => setNewCustomLinkName(e.target.value)}
                              className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none transition-all"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block ml-1">URL (Endereço)</label>
                            <input 
                              type="text"
                              placeholder="https://exemplo.com"
                              value={newCustomLinkUrl}
                              onChange={(e) => setNewCustomLinkUrl(e.target.value)}
                              className="w-full p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 focus:border-indigo-400 outline-none transition-all"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2.5">
                          <button 
                            onClick={() => {
                              setShowAddCustomLink(false);
                              setNewCustomLinkName('');
                              setNewCustomLinkUrl('');
                            }}
                            className="px-4 py-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer"
                          >
                            Cancelar
                          </button>
                          <button 
                            onClick={() => {
                              if (!newCustomLinkName || !newCustomLinkUrl) return;
                              let url = newCustomLinkUrl;
                              if (!/^https?:\/\//i.test(url)) {
                                url = 'https://' + url;
                              }
                              const updated = [...customLinks, { id: Date.now().toString(), name: newCustomLinkName, url }];
                              setCustomLinks(updated);
                              scopedSet('allodos-custom-links', JSON.stringify(updated));
                              setNewCustomLinkName('');
                              setNewCustomLinkUrl('');
                              setShowAddCustomLink(false);
                            }}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md cursor-pointer"
                          >
                            Adicionar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Custom Links List */}
                    <div className="space-y-3">
                      {customLinks.map((link) => (
                        <div 
                          key={link.id} 
                          className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:border-indigo-200 dark:hover:border-indigo-950 transition-all shadow-sm"
                        >
                          <div className="space-y-0.5 min-w-0 flex-1 pr-3">
                            <h6 className="text-xs font-black text-slate-800 dark:text-slate-200 truncate">{link.name}</h6>
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noreferrer"
                              className="text-[10px] font-semibold text-slate-400 hover:text-indigo-600 dark:text-slate-500 dark:hover:text-indigo-400 flex items-center gap-1 truncate"
                            >
                              <Globe size={10} className="shrink-0" /> <span className="truncate">{link.url}</span>
                            </a>
                          </div>
                          <button 
                            onClick={() => {
                              const updated = customLinks.filter(l => l.id !== link.id);
                              setCustomLinks(updated);
                              scopedSet('allodos-custom-links', JSON.stringify(updated));
                            }}
                            className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-all cursor-pointer"
                            title="Remover Link"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      {customLinks.length === 0 && (
                        <div className="text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl select-none">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Nenhum link personalizado cadastrado</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SPLIT WORKSPACE: EDITOR DO PORTAL (30%) E PRÉ-VISUALIZAÇÃO (70%) */}
                <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 items-start w-full min-w-0 animate-in fade-in duration-300">
                  {/* EDITOR DO PORTAL (30% -> col-span-3) */}
                  <div className="lg:col-span-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] shadow-sm space-y-5 w-full min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
                      <Pencil size={16} className="text-indigo-500" />
                      <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Editor do Portal</h4>
                    </div>

                    {/* Stacked layout inside the 30% Portal Editor */}
                    <div className="space-y-5 min-w-0">
                    {/* Left Col: Welcome Message */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                        Mensagem de Boas-vindas
                      </label>
                      <textarea
                        value={welcomeMessage}
                        onChange={(e) => {
                          setWelcomeMessage(e.target.value);
                          scopedSet('allodos-welcome-message', e.target.value);
                        }}
                        className="w-full max-w-full resize-y p-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-2xl text-xs font-bold text-slate-800 dark:text-slate-100 min-h-[140px] outline-none focus:border-indigo-400 transition-all font-sans leading-relaxed"
                        placeholder="Escreva a mensagem que os pacientes verão no portal..."
                      />
                    </div>

                    {/* Right Col: Style Visual do Portal */}
                    <div className="space-y-4">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                        Estilo Visual do Portal
                      </label>

                      {/* INFORMAÇÕES COMPLEMENTARES (FIRST AREA TO BE FORMATTED) */}
                      <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl space-y-3.5 min-w-0 overflow-hidden">
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 block ml-1">
                            Informações Complementares
                          </label>
                          <textarea
                            value={rightSideText}
                            onChange={(e) => {
                              setRightSideText(e.target.value);
                              scopedSet('allodos-right-text', e.target.value);
                            }}
                            className="w-full max-w-full resize-y p-3 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-200 min-h-[70px] outline-none focus:border-indigo-400 transition-all font-sans leading-relaxed"
                            placeholder="Escreva as informações da clínica que aparecerão do lado direito do portal..."
                          />
                        </div>

                        {/* Font Family selection */}
                        <div className="grid grid-cols-1 gap-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                              Família da Fonte
                            </label>
                            <select
                              value={compFontFamily}
                              onChange={(e) => {
                                setCompFontFamily(e.target.value);
                                scopedSet('allodos-comp-font-family', e.target.value);
                              }}
                              className="w-full p-2 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 outline-none focus:border-indigo-400 transition-all cursor-pointer"
                            >
                              {FONT_FAMILIES.map((font) => (
                                <option key={font.value} value={font.value}>
                                  {font.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Font Size & Styles */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                                Tamanho da Fonte
                              </label>
                              <select
                                value={compFontSize}
                                onChange={(e) => {
                                  setCompFontSize(e.target.value);
                                  scopedSet('allodos-comp-font-size', e.target.value);
                                }}
                                className="w-full p-2 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 outline-none focus:border-indigo-400 transition-all cursor-pointer"
                              >
                                {FONT_SIZES.map((size) => (
                                  <option key={size.value} value={size.value}>
                                    {size.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-1">
                              <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                                Estilo da Escrita
                              </label>
                              <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-xl border border-slate-200/50 dark:border-slate-700/50 h-[34px]">
                                {/* Bold Toggle */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = !compIsBold;
                                    setCompIsBold(next);
                                    scopedSet('allodos-comp-is-bold', String(next));
                                  }}
                                  className={`flex-1 flex items-center justify-center rounded-lg text-xs transition-all cursor-pointer ${
                                    compIsBold
                                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs font-black'
                                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                  }`}
                                  title="Negrito"
                                >
                                  B
                                </button>
                                {/* Italic Toggle */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = !compIsItalic;
                                    setCompIsItalic(next);
                                    scopedSet('allodos-comp-is-italic', String(next));
                                  }}
                                  className={`flex-1 flex items-center justify-center rounded-lg text-xs transition-all cursor-pointer ${
                                    compIsItalic
                                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs italic font-black'
                                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                  }`}
                                  title="Itálico"
                                >
                                  I
                                </button>
                                {/* Underline Toggle */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = !compIsUnderline;
                                    setCompIsUnderline(next);
                                    scopedSet('allodos-comp-is-underline', String(next));
                                  }}
                                  className={`flex-1 flex items-center justify-center rounded-lg text-xs transition-all cursor-pointer underline ${
                                    compIsUnderline
                                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs font-black'
                                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                  }`}
                                  title="Sublinhado"
                                >
                                  U
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Bloco #69: Cor do texto da faixa lateral */}
                          <div className="space-y-1">
                            <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                              Cor do Texto
                            </label>
                            <div className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2">
                              <input
                                type="color"
                                aria-label="Escolher a cor do texto"
                                value={(compTextColor || '#FFFFFF').toLowerCase().length === 7 ? (compTextColor || '#FFFFFF').toLowerCase() : '#ffffff'}
                                onChange={(e) => applyTextColor(e.target.value)}
                                className="w-8 h-8 rounded-lg cursor-pointer border border-slate-200 dark:border-slate-800 p-0"
                              />
                              <input
                                type="text"
                                aria-label="Código hexadecimal da cor do texto"
                                aria-invalid={!!compTextColorError}
                                value={compTextColorDraft}
                                maxLength={7}
                                onChange={(e) => applyTextColor(e.target.value)}
                                placeholder="#FFFFFF"
                                className={`w-full min-w-0 p-2 bg-white dark:bg-slate-850 border rounded-xl text-[10px] font-bold text-slate-700 dark:text-slate-300 outline-none ${compTextColorError ? 'border-rose-400' : 'border-slate-200 dark:border-slate-800'}`}
                              />
                              <div className="flex gap-1">
                                {['#FFFFFF', '#0F172A'].map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => applyTextColor(c)}
                                    title={c === '#FFFFFF' ? 'Branco' : 'Preto'}
                                    aria-label={c === '#FFFFFF' ? 'Texto branco' : 'Texto preto'}
                                    className="w-6 h-6 rounded-md border border-slate-300 dark:border-slate-600 cursor-pointer"
                                    style={{ background: c }}
                                  />
                                ))}
                              </div>
                            </div>
                            {compTextColorError && <p role="alert" className="text-[10px] font-bold text-rose-600 ml-1">{compTextColorError}</p>}
                          </div>
                        </div>
                      </div>
                      
                      {/* Style selection buttons inside Custom Text style customization block to let user choose one or the other */}
                      <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl space-y-4 min-w-0 overflow-hidden">
                        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80 pb-2">
                          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 leading-snug">Tipo de Conteúdo Lateral</span>
                          <div className="grid grid-cols-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full xl:w-auto min-w-0">
                            <button
                              type="button"
                              onClick={() => {
                                setRightSideStyle('blank');
                                scopedSet('allodos-right-style', 'blank');
                              }}
                              className={`min-w-0 px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                                rightSideStyle === 'blank'
                                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                                  : 'text-slate-500'
                              }`}
                            >
                              Vazio
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRightSideStyle('custom-text');
                                scopedSet('allodos-right-style', 'custom-text');
                              }}
                              className={`min-w-0 px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider leading-tight transition-all cursor-pointer ${
                                rightSideStyle === 'custom-text'
                                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                                  : 'text-slate-500'
                              }`}
                            >
                              Texto personalizado
                            </button>
                          </div>
                        </div>

                        {/* Background customization (Gradient/Image) block inside visual style settings, co-existing beautifully with selected content style */}
                        <div className="space-y-3">
                          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 leading-snug">Imagem ou Gradiente de Fundo</span>
                            <div className="grid grid-cols-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full xl:w-auto min-w-0">
                              <button
                                type="button"
                                onClick={() => {
                                  setRightSideBgType('gradient');
                                  scopedSet('allodos-right-bg-type', 'gradient');
                                }}
                                className={`min-w-0 px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-1 ${
                                  rightSideBgType === 'gradient'
                                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                                    : 'text-slate-500'
                                }`}
                              >
                                <Palette size={10} /> Gradiente
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRightSideBgType('image');
                                  scopedSet('allodos-right-bg-type', 'image');
                                }}
                                className={`min-w-0 px-2 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-1 ${
                                  rightSideBgType === 'image'
                                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                                    : 'text-slate-500'
                                }`}
                              >
                                <ImageIcon size={10} /> Imagem
                              </button>
                            </div>
                          </div>

                          {/* Conditional Background Options */}
                          {rightSideBgType === 'gradient' && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 pt-1 animate-in fade-in duration-200">
                              <div className="space-y-1">
                                <label className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Cor Inicial</label>
                                <div className="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-2">
                                  <input 
                                    type="color" 
                                    value={gradientColor1}
                                    onChange={(e) => {
                                      setGradientColor1(e.target.value);
                                      scopedSet('allodos-right-gradient-1', e.target.value);
                                    }}
                                    className="w-8 h-8 rounded-lg cursor-pointer border border-slate-200 dark:border-slate-800 p-0"
                                  />
                                  <input 
                                    type="text" 
                                    value={gradientColor1}
                                    onChange={(e) => {
                                      setGradientColor1(e.target.value);
                                      scopedSet('allodos-right-gradient-1', e.target.value);
                                    }}
                                    className="w-full min-w-0 p-2 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl text-[10px] font-bold text-slate-700 dark:text-slate-300 outline-none"
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <label className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Cor Final</label>
                                <div className="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-2">
                                  <input 
                                    type="color" 
                                    value={gradientColor2}
                                    onChange={(e) => {
                                      setGradientColor2(e.target.value);
                                      scopedSet('allodos-right-gradient-2', e.target.value);
                                    }}
                                    className="w-8 h-8 rounded-lg cursor-pointer border border-slate-200 dark:border-slate-800 p-0"
                                  />
                                  <input 
                                    type="text" 
                                    value={gradientColor2}
                                    onChange={(e) => {
                                      setGradientColor2(e.target.value);
                                      scopedSet('allodos-right-gradient-2', e.target.value);
                                    }}
                                    className="w-full min-w-0 p-2 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl text-[10px] font-bold text-slate-700 dark:text-slate-300 outline-none"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {rightSideBgType === 'image' && (
                            <div className="space-y-3 pt-1 animate-in fade-in duration-200">
                              <label className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                                Imagem de Fundo da Lateral
                              </label>
                              
                              <div
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  setIsDragging(true);
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault();
                                  setIsDragging(false);
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  setIsDragging(false);
                                  const files = e.dataTransfer.files;
                                  if (files && files[0]) {
                                    handleImageFile(files[0]);
                                  }
                                }}
                                onClick={() => document.getElementById('bg-file-upload')?.click()}
                                className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2 group ${
                                  isDragging
                                    ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/10'
                                    : backgroundImageUrl
                                      ? 'border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/10 dark:bg-indigo-950/5 hover:border-indigo-300'
                                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-850'
                                }`}
                              >
                                <input
                                  type="file"
                                  id="bg-file-upload"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files && files[0]) {
                                      handleImageFile(files[0]);
                                    }
                                  }}
                                  className="hidden"
                                />

                                {backgroundImageUrl ? (
                                  <div className="w-full space-y-3">
                                    <div className="relative w-full h-24 rounded-xl overflow-hidden border border-slate-100 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                                      <img 
                                        src={backgroundImageUrl} 
                                        alt="Pré-visualização" 
                                        className="w-full h-full object-cover"
                                        referrerPolicy="no-referrer"
                                      />
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-white">Alterar Imagem</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-between text-[10px]">
                                      <span className="font-semibold text-slate-500 dark:text-slate-400 truncate max-w-[150px]">
                                        {backgroundImageUrl.startsWith('data:') ? 'Imagem carregada' : 'Imagem de internet'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setBackgroundImageUrl('');
                                          scopedRemove('allodos-right-bg-image');
                                        }}
                                        className="text-red-500 hover:text-red-600 font-black uppercase tracking-wider flex items-center gap-1 cursor-pointer animate-in fade-in"
                                      >
                                        <Trash2 size={12} /> Remover
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-full text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                                      <Upload size={18} />
                                    </div>
                                    <div className="space-y-1">
                                      <p className="text-[11px] font-black text-slate-700 dark:text-slate-300">
                                        Arraste o arquivo aqui ou <span className="text-indigo-600 dark:text-indigo-400">escolha do computador</span>
                                      </p>
                                      <p className="text-[9px] font-medium text-slate-400 dark:text-slate-500">
                                        Suporta PNG, JPG ou GIF (máx. 5MB)
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>


                            </div>
                          )}

                          {/* Transparency Selector Option */}
                          <div className="space-y-1.5 pt-3 border-t border-slate-100 dark:border-slate-800/80 animate-in fade-in duration-200">
                            <div className="flex items-center justify-between">
                              <label className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">
                                Transparência do Fundo
                              </label>
                              <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                                {bgTransparency}%
                              </span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <input 
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={bgTransparency}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setBgTransparency(val);
                                  scopedSet('allodos-right-bg-transparency', String(val));
                                }}
                                className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer"
                              />
                              <div className="flex gap-1 justify-between">
                                {[0, 20, 50, 80, 100].map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => {
                                      setBgTransparency(preset);
                                      scopedSet('allodos-right-bg-transparency', String(preset));
                                    }}
                                    className={`flex-1 py-1 rounded border text-[8px] font-black transition-all cursor-pointer ${
                                      bgTransparency === preset
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white dark:bg-slate-850 text-slate-500 border-slate-200 dark:border-slate-800 hover:bg-slate-100'
                                    }`}
                                  >
                                    {preset}%
                                  </button>
                                ))}
                              </div>
                            </div>
                            <p className="text-[8px] font-medium text-slate-400 dark:text-slate-500 ml-1">
                              0% = Opaco (cor sólida). 100% = Totalmente invisível.
                            </p>
                          </div>
                        </div>


                      </div>
                    </div>
                  </div>
                </div>

              {/* PRÉ-VISUALIZAÇÃO INTERATIVA (PORTAL DO PACIENTE - 70% -> col-span-7) */}
              <div className="lg:col-span-7 w-full space-y-4">
                  <div className="flex items-center justify-between ml-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Pré-visualização Interativa (Portal do Paciente)</span>
                    <button 
                      onClick={() => {
                        setPreviewStep('landing');
                        setSelectedProf(null);
                        setSelectedDay(15);
                        setSelectedTimeSlot(null);
                      }}
                      className="text-[9px] font-bold text-indigo-600 hover:underline cursor-pointer"
                    >
                      Reiniciar Simulação
                    </button>
                  </div>

                  {/* Device-mock Wrapper */}
                  <div className="border-4 border-slate-300 dark:border-slate-800 rounded-[2rem] overflow-hidden bg-slate-100 dark:bg-slate-950 shadow-2xl relative">
                    
                    {/* Simulated Browser Bar */}
                    <div className="bg-slate-200 dark:bg-slate-900 px-4 py-2.5 flex items-center gap-2 border-b border-slate-300 dark:border-slate-800 select-none">
                      <div className="flex gap-1">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block"></span>
                        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block"></span>
                        <span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block"></span>
                      </div>
                      <div className="flex-1 max-w-sm mx-auto bg-white/70 dark:bg-slate-800/80 rounded-lg px-3 py-1 text-[9px] font-bold font-mono text-slate-500 dark:text-slate-400 text-center truncate">
                        {bookingLinkPrefix}{slug || 'nome-da-clinica'}
                      </div>
                    </div>

                    {/* SIMULATOR WINDOW VIEWPORT CONTAINER */}
                    <div className="min-h-[460px] bg-slate-50 dark:bg-slate-900 flex flex-col font-sans text-slate-800 dark:text-slate-100 overflow-y-auto">
                      
                      {/* STEP 1: PORTAL OPENING PAGE (PÁGINA DE ABERTURA) */}
                      {previewStep === 'landing' && (() => {
                        // Bloco 35: idêntico ao link externo — mesmos componentes do PatientPortal,
                        // alimentados pelo que será publicado (buildBookingConfig).
                        const cfg = buildBookingConfig();
                        return (
                          <div className="flex-1 flex flex-col md:flex-row bg-slate-50 dark:bg-slate-950 min-h-[420px]">
                            <BookingLandingAside compact clinic={cfg.clinic} appearance={cfg.appearance} />
                            <main className="flex-1 p-5 overflow-y-auto">
                              <div className="max-w-md mx-auto space-y-4">
                                <h2 className="text-sm font-black text-slate-900 dark:text-white">Escolha o profissional</h2>
                                {bookingProfs.length === 0 && (
                                  <p className="text-[11px] text-slate-400 font-medium">Nenhum profissional ativo para exibir.</p>
                                )}
                                {bookingProfs.map((prof) => (
                                  <ProfessionalCard
                                    key={prof.id}
                                    compact
                                    prof={prof}
                                    onSelect={() => {
                                      setSelectedProf(prof);
                                      setPreviewStep('calendar');
                                    }}
                                  />
                                ))}
                              </div>
                            </main>
                          </div>
                        );
                      })()}

                      {/* STEP 2: MONTHLY CALENDAR VIEW (DIAS COM HORÁRIO EM CINZA CLARO SE INDISPONÍVEL) */}
                      {previewStep === 'calendar' && selectedProf && (
                        <div className="flex-1 p-5 flex flex-col justify-between animate-in fade-in slide-in-from-right-3 duration-300 bg-white dark:bg-slate-900">
                          <div className="space-y-4">
                            
                            {/* Simulator header back button */}
                            <button
                              onClick={() => {
                                setPreviewStep('landing');
                                setSelectedProf(null);
                                setSelectedTimeSlot(null);
                              }}
                              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 cursor-pointer transition-all"
                            >
                              <ChevronLeft size={14} /> Escolher Profissional
                            </button>

                            {/* Professional Header Mini-Banner */}
                            <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/40 p-3 rounded-2xl border border-slate-100 dark:border-slate-800">
                              <ProfessionalAvatar name={selectedProf.name} photo={selectedProf.photo} className="w-10 h-10 rounded-xl" />
                              <div>
                                <span className="text-[8px] bg-indigo-500 text-white px-1.5 py-0.5 rounded font-black uppercase tracking-wider block w-fit mb-0.5">Agendamento ativo</span>
                                <h4 className="text-xs font-black text-slate-800 dark:text-slate-100">{selectedProf.name}</h4>
                                <p className="text-[9px] text-slate-400 font-semibold">{selectedProf.formation}</p>
                              </div>
                            </div>

                            <div className="pt-2 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={handlePrevMonth}
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-all cursor-pointer animate-none"
                                    title="Mês Anterior"
                                  >
                                    <ChevronLeft size={14} />
                                  </button>
                                  <h5 className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider min-w-[100px] text-center">
                                    {MONTHS_NAMES[currentMonth]} {currentYear}
                                  </h5>
                                  <button
                                    type="button"
                                    onClick={handleNextMonth}
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-all cursor-pointer animate-none"
                                    title="Próximo Mês"
                                  >
                                    <ChevronRight size={14} />
                                  </button>
                                </div>
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Selecione o Dia</span>
                              </div>

                              {/* Calendar layout representing currentMonth/currentYear */}
                              <div className="space-y-1">
                                {/* Weekday Headers */}
                                <div className="grid grid-cols-7 gap-1 text-center font-black text-[9px] text-slate-400 uppercase tracking-widest py-1 border-b border-slate-100 dark:border-slate-800">
                                  <span>D</span>
                                  <span>S</span>
                                  <span>T</span>
                                  <span>Q</span>
                                  <span>Q</span>
                                  <span>S</span>
                                  <span>S</span>
                                </div>

                                {/* Calendar grid of days. */}
                                <div className="grid grid-cols-7 gap-1 pt-1.5 text-center">
                                  {/* Dynamic Offsets */}
                                  {Array.from({ length: new Date(currentYear, currentMonth, 1).getDay() }).map((_, idx) => (
                                    <span key={`offset-${idx}`} className="p-2 opacity-0"></span>
                                  ))}

                                  {/* Days of current month */}
                                  {Array.from({ length: new Date(currentYear, currentMonth + 1, 0).getDate() }, (_, i) => {
                                    const dayNum = i + 1;
                                    const weekdayIndex = new Date(currentYear, currentMonth, dayNum).getDay();
                                    const isWeekend = weekdayIndex === 0 || weekdayIndex === 6; // 0 = Sunday, 6 = Saturday
                                    const isSelected = selectedDay === dayNum;

                                    const daySlots = getAvailableSlotsForPreview(dayNum);
                                    const isDisabled = isWeekend || daySlots.length === 0;

                                    return (
                                      <button
                                        type="button"
                                        key={dayNum}
                                        disabled={isDisabled}
                                        onClick={() => setSelectedDay(dayNum)}
                                        className={`p-2 rounded-lg text-[11px] font-black transition-all cursor-pointer ${
                                          isDisabled 
                                            ? 'text-slate-300 dark:text-slate-700 bg-slate-50/40 dark:bg-slate-900/20 cursor-not-allowed opacity-35' 
                                            : isSelected 
                                              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/10' 
                                              : 'text-slate-700 dark:text-slate-300 bg-indigo-50/50 hover:bg-indigo-100 dark:bg-slate-800/40 dark:hover:bg-slate-800 border border-slate-200/50 dark:border-slate-700/50'
                                        }`}
                                      >
                                        {dayNum}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Horários de Atendimento Disponíveis */}
                            {selectedDay && (
                              <div className="pt-2.5 border-t border-slate-100 dark:border-slate-800 animate-in fade-in duration-200 space-y-2">
                                <h6 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block">
                                  Horários Disponíveis (Dia {selectedDay} de {MONTHS_NAMES[currentMonth]})
                                </h6>
                                <div className="grid grid-cols-4 gap-2">
                                  {getAvailableSlotsForPreview(selectedDay).length > 0 ? (
                                    getAvailableSlotsForPreview(selectedDay).map((time) => (
                                      <button
                                        key={time}
                                        onClick={() => {
                                          setSelectedTimeSlot(time);
                                          setPreviewStep('popup');
                                        }}
                                        className="p-2 bg-slate-100 hover:bg-indigo-50 hover:border-indigo-300 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-black text-slate-800 dark:text-slate-100 transition-all text-center cursor-pointer hover:scale-[1.03]"
                                      >
                                        {time}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="col-span-4 text-center py-4 bg-slate-50 dark:bg-slate-950/20 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Sem horários neste dia</span>
                                    </div>
                                  )}
                                </div>
                                <p className="text-[8px] text-slate-400 font-medium italic mt-1 leading-normal">
                                  * Horários calculados de acordo com seu intervalo de {minInterval}min e antecedência de {minAdvanceHours}h.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* STEP 3: MODAL POPUP FOR MODALITY, SERVICE AND NAME */}
                      {previewStep === 'popup' && selectedProf && selectedDay && selectedTimeSlot && (
                        <div className="flex-1 p-5 flex flex-col justify-between bg-white dark:bg-slate-900 relative">
                          {/* Calendar view is blurred underneath */}
                          <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/50 backdrop-blur-sm z-10 flex items-center justify-center p-4">
                            <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 shadow-2xl space-y-4 animate-in zoom-in-95 duration-200 z-20">
                              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                                <h5 className="text-xs font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest flex items-center gap-1">
                                  <CheckCircle size={14} className="text-indigo-500" /> Confirmar Consulta
                                </h5>
                                <button 
                                  onClick={() => setPreviewStep('calendar')} 
                                  className="text-xs font-black text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                                >
                                  &times;
                                </button>
                              </div>

                              <div className="space-y-3.5">
                                {/* Informative scheduling summary */}
                                <div className="text-[10px] bg-indigo-50 dark:bg-indigo-950/30 p-2.5 rounded-xl border border-indigo-100/50 dark:border-indigo-900/30 text-indigo-900 dark:text-indigo-300 space-y-0.5 font-bold">
                                  <div>Profissional: <span className="font-black text-indigo-950 dark:text-indigo-100">{selectedProf.name}</span></div>
                                  <div>Data: <span className="font-black text-indigo-950 dark:text-indigo-100">{selectedDay} de {MONTHS_NAMES[currentMonth]}, {currentYear}</span></div>
                                  <div>Horário: <span className="font-black text-indigo-950 dark:text-indigo-100">{selectedTimeSlot}</span></div>
                                </div>

                                {/* Choose Modality */}
                                <div className="space-y-1.5">
                                  <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Modalidade de Atendimento</label>
                                  <div className="grid grid-cols-2 gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setSelectedModality('presencial')}
                                      className={`p-2.5 rounded-xl border flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                                        selectedModality === 'presencial'
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'
                                      }`}
                                    >
                                      🏢 Presencial
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setSelectedModality('online')}
                                      className={`p-2.5 rounded-xl border flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                                        selectedModality === 'online'
                                          ? 'bg-indigo-600 text-white border-indigo-600'
                                          : 'bg-slate-50 dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'
                                      }`}
                                    >
                                      💻 Online
                                    </button>
                                  </div>
                                </div>

                                {/* Choose Service from list */}
                                <div className="space-y-1">
                                  <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Escolha o Serviço</label>
                                  <select
                                    value={selectedServiceId}
                                    onChange={(e) => setSelectedServiceId(e.target.value)}
                                    className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:border-indigo-500"
                                  >
                                    {services.filter(s => s.active !== false).map((svc) => (
                                      <option key={svc.id} value={svc.id}>
                                        {svc.name} - R$ {svc.price} ({svc.duration} min)
                                      </option>
                                    ))}
                                    {services.filter(s => s.active !== false).length === 0 && (
                                      <option value="default">Consulta de Avaliação</option>
                                    )}
                                  </select>
                                </div>

                                {/* Personal Fields */}
                                <div className="space-y-2">
                                  <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Seu Nome</label>
                                    <input 
                                      type="text"
                                      value={patientName}
                                      onChange={(e) => setPatientName(e.target.value)}
                                      placeholder="Ex: João Silva"
                                      className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 outline-none"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block ml-1">Celular / WhatsApp</label>
                                    <input 
                                      type="text"
                                      value={patientPhone}
                                      onChange={(e) => setPatientPhone(e.target.value)}
                                      placeholder="Ex: (11) 99999-9999"
                                      className="w-full p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 outline-none"
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="flex gap-2.5 pt-2">
                                <button
                                  onClick={() => setPreviewStep('calendar')}
                                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer"
                                >
                                  Voltar
                                </button>
                                <button
                                  onClick={() => setPreviewStep('whatsapp')}
                                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md shadow-emerald-600/10 transition-all cursor-pointer text-center"
                                >
                                  Confirmar (OK)
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 4: WHATSAPP MESSAGE RECEIVED SIMULATOR */}
                      {previewStep === 'whatsapp' && selectedProf && selectedDay && selectedTimeSlot && (
                        <div className="flex-1 flex flex-col bg-[#efeae2] dark:bg-slate-950 text-slate-900 dark:text-slate-100 animate-in fade-in duration-300">
                          
                          {/* WhatsApp top bar */}
                          <div className="bg-emerald-600 dark:bg-emerald-950 text-white px-4 py-3 flex items-center justify-between shadow-md">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-emerald-800 flex items-center justify-center font-black text-[10px]">
                                AD
                              </div>
                              <div>
                                <h5 className="text-[11px] font-black leading-tight">Allodos Clínicas</h5>
                                <span className="text-[8px] opacity-85 block leading-none">online</span>
                              </div>
                            </div>
                            <div className="flex gap-3 text-white/90">
                              <span className="text-xs font-bold">💬</span>
                              <span className="text-xs font-bold">⋮</span>
                            </div>
                          </div>

                          {/* Chat Conversation Body */}
                          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                            <div className="text-center">
                              <span className="bg-indigo-50/80 dark:bg-indigo-950/30 text-indigo-900 dark:text-indigo-300 px-3 py-1.5 rounded-xl text-[8px] font-bold uppercase tracking-wider shadow-sm select-none">
                                Consulta Agendada com Sucesso!
                              </span>
                            </div>

                            {/* WhatsApp Bubble */}
                            <div className="max-w-[85%] bg-white dark:bg-slate-900 border border-slate-200/50 dark:border-slate-800 p-3.5 rounded-2xl shadow-sm text-xs space-y-2 relative ml-1 animate-in slide-in-from-bottom-2 duration-300">
                              {/* Message bubble tail indicator */}
                              <div className="absolute left-[-6px] top-4 w-3 h-3 bg-white dark:bg-slate-900 border-l border-b border-slate-200/50 dark:border-slate-800 rotate-45"></div>

                              <p className="font-medium text-slate-700 dark:text-slate-300">
                                Olá <span className="font-black text-indigo-600 dark:text-indigo-400">*{patientName}*</span>! Seu agendamento foi confirmado automaticamente pelo Allodos.
                              </p>

                              <div className="bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl text-[11px] space-y-1 border border-slate-100 dark:border-slate-700 font-semibold text-slate-600 dark:text-slate-300">
                                <div>📅 <span className="font-bold">Data:</span> {selectedDay} de {MONTHS_NAMES[currentMonth]}, {currentYear}</div>
                                <div>⏰ <span className="font-bold">Horário:</span> {selectedTimeSlot}</div>
                                <div>🩺 <span className="font-bold">Profissional:</span> {selectedProf.name}</div>
                                <div>🎯 <span className="font-bold">Serviço:</span> {services.find(s => s.id === selectedServiceId)?.name || 'Consulta de Psicoterapia'}</div>
                                <div>🏢 <span className="font-bold">Modalidade:</span> {selectedModality === 'online' ? '💻 Online (Videochamada)' : '🏢 Presencial'}</div>
                                <div className="truncate">📍 <span className="font-bold">Local/Meet:</span> {selectedModality === 'online' ? 'allodos.com/meet/sala-virtual-tele' : 'Av. Paulista, 1000 - Conjunto 52, SP'}</div>
                              </div>

                              <p className="text-[10px] text-slate-400 font-semibold italic">
                                * Mensagem de confirmação enviada via WhatsApp para {patientPhone}.
                              </p>
                            </div>
                          </div>

                          {/* Simulator footer reset buttons */}
                          <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                            <button
                              onClick={() => {
                                setPreviewStep('landing');
                                setSelectedProf(null);
                                setSelectedDay(15);
                                setSelectedTimeSlot(null);
                              }}
                              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer shadow-md"
                            >
                              Fazer Novo Agendamento
                            </button>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Página de Agendamento ativa</span>
                </div>
                <button
                  type="button"
                  onClick={handleOpenBookingPage}
                  disabled={isPublishing || !slug}
                  className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 disabled:opacity-50"
                >
                  {isPublishing ? 'Publicando...' : 'Visualizar Página'} <ExternalLink size={12} />
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Sidebar Creation Forms / Help Info (takes 1 column) */}
        {activeTab !== 'booking-link' && (
          <div className="space-y-6">
            
            {/* Create New Item forms depend on active tab */}
            {activeTab === 'services' && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] shadow-sm space-y-6 animate-in fade-in duration-300">
                <h4 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                  {editingService ? 'Editar Serviço' : 'Novo Serviço'}
                </h4>
                <form onSubmit={handleAddService} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Nome do Serviço</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ex: Consulta Psiquiátrica"
                      value={newServiceName}
                      onChange={(e) => setNewServiceName(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-semibold focus:border-indigo-500 dark:focus:border-indigo-500 outline-none transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Duração (min)</label>
                      <input 
                        type="number" 
                        required
                        min="10"
                        max="300"
                        value={newServiceDuration}
                        onChange={(e) => setNewServiceDuration(Number(e.target.value))}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-bold focus:border-indigo-500 outline-none transition-colors"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Preço (R$)</label>
                      <input 
                        type="number" 
                        required
                        min="0"
                        value={newServicePrice}
                        onChange={(e) => setNewServicePrice(Number(e.target.value))}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-bold focus:border-indigo-500 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Descrição</label>
                    <textarea 
                      rows={3}
                      placeholder="Descreva brevemente o serviço oferecido."
                      value={newServiceDesc}
                      onChange={(e) => setNewServiceDesc(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-semibold focus:border-indigo-500 outline-none transition-colors resize-none"
                    />
                  </div>

                  {/* Bloco 36: vínculo do serviço com os profissionais */}
                  <ProfessionalPicker
                    question="Quais profissionais oferecerão esse serviço?"
                    professionals={sortedProfs}
                    selected={newServiceProfIds}
                    onChange={setNewServiceProfIds}
                  />

                  <div className="flex flex-col gap-2 pt-2">
                    <button 
                      type="submit"
                      className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-indigo-600/15"
                    >
                      {editingService ? <Check size={14} /> : <Plus size={14} />}
                      {editingService ? 'Salvar Alterações' : 'Adicionar Serviço'}
                    </button>
                    {editingService && (
                      <button 
                        type="button"
                        onClick={handleCancelEdit}
                        className="w-full py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'locations' && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] shadow-sm space-y-6 animate-in fade-in duration-300">
                <h4 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                  {editingLocation ? 'Editar Local' : 'Novo Local'}
                </h4>
                <form onSubmit={handleAddLocation} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Nome do Local</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ex: Consultório Particular"
                      value={newLocName}
                      onChange={(e) => setNewLocName(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-semibold focus:border-indigo-500 outline-none transition-colors"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Tipo de Atendimento</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setNewLocType('online')}
                        className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                          newLocType === 'online' 
                            ? 'bg-indigo-600 text-white border-indigo-600' 
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center justify-center gap-1.5"><Globe size={12}/> Online</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewLocType('presential')}
                        className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                          newLocType === 'presential' 
                            ? 'bg-indigo-600 text-white border-indigo-600' 
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center justify-center gap-1.5"><MapPin size={12}/> Presencial</span>
                      </button>
                    </div>
                  </div>

                  {newLocType === 'online' && (
                    <div className="space-y-1.5 animate-in fade-in duration-200">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Link da sala virtual (Google Meet)</label>
                      <input
                        type="url"
                        required
                        placeholder="https://meet.google.com/abc-defg-hij"
                        pattern="https?://.*"
                        value={newLocLink}
                        onChange={(e) => setNewLocLink(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-semibold focus:border-indigo-500 outline-none transition-colors"
                      />
                      <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                        Crie uma sala fixa no Google Meet (meet.google.com → "Nova reunião" → "Criar uma reunião para depois") e cole o link aqui. Ao clicar em "Acessar Sala", este link é enviado ao paciente por WhatsApp e e-mail e aberto para você.
                      </p>
                    </div>
                  )}

                  {newLocType === 'presential' && (
                    <div className="space-y-1.5 animate-in fade-in duration-200">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Endereço Completo</label>
                      <textarea 
                        rows={3}
                        required
                        placeholder="Ex: Av. Paulista, 1000 - Conj. 52, São Paulo - SP"
                        value={newLocAddress}
                        onChange={(e) => setNewLocAddress(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 px-4 py-3 rounded-xl text-slate-800 dark:text-slate-100 text-xs font-semibold focus:border-indigo-500 outline-none transition-colors resize-none"
                      />
                    </div>
                  )}

                  {/* Bloco 36: vínculo do local com os profissionais */}
                  <ProfessionalPicker
                    question="Quais profissionais atenderão nesse local?"
                    professionals={sortedProfs}
                    selected={newLocProfIds}
                    onChange={setNewLocProfIds}
                  />

                  <div className="flex flex-col gap-2 pt-2">
                    <button 
                      type="submit"
                      className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-indigo-600/15"
                    >
                      {editingLocation ? <Check size={14} /> : <Plus size={14} />}
                      {editingLocation ? 'Salvar Alterações' : 'Adicionar Local'}
                    </button>
                    {editingLocation && (
                      <button 
                        type="button"
                        onClick={handleCancelEditLocation}
                        className="w-full py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all cursor-pointer"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* Quick info guides */}
            <div className="bg-slate-100/50 dark:bg-slate-900/30 border border-slate-200/50 dark:border-slate-800/50 p-4 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] space-y-4">
              <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Dica Prática</h4>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold leading-relaxed">
                Manter seus serviços, locais e horários atualizados garante que novos pacientes agendem consultas diretamente pelo seu link público, poupando tempo em trocas manuais de mensagens.
              </p>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};
